// src/modules/workspace/NLPDeepLab.jsx
// ─────────────────────────────────────────────────────────────────────────────
// NLP DEEP ANALYSIS — advanced text intelligence for real-world data
// subTab routing: deep_setup · deep_emotion · deep_intent · deep_clusters
//                 deep_trends · deep_signals · deep_voice · deep_compare
//                 deep_annotate · report (nlp_deep)
// ─────────────────────────────────────────────────────────────────────────────
import React, { useState, useMemo, useCallback, useRef, useEffect } from 'react';
import {
  BarChart, Bar, LineChart, Line, AreaChart, Area,
  RadarChart, Radar, PolarGrid, PolarAngleAxis, PolarRadiusAxis,
  ScatterChart, Scatter, PieChart, Pie,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell, Legend,
} from 'recharts';
import { C, PALETTE, inferTypes, Btn, Label, ChartTip, PinButton } from './wsShared';

// ─────────────────────────────────────────────────────────────────────────────
// DEEP NLP ENGINE
// ─────────────────────────────────────────────────────────────────────────────
const STOPWORDS = new Set(['the','a','an','and','or','but','in','on','at','to','for','of','with','by','from','up','about','into','through','is','are','was','were','be','been','being','have','has','had','do','does','did','will','would','could','should','may','might','can','i','you','he','she','it','we','they','me','him','her','us','them','this','that','these','those','what','which','who','how','all','each','every','both','not','no','so','yet','also','just','very','still','than','then','if','as','its','my','your','his','our','their','more','most','other','some','any','now','here','there','am','get','got','say','make','like','see','know','go','one','two','said','been']);

function tok(text, sw=true) {
  const w = String(text||'').toLowerCase().replace(/https?:\/\/\S+/g,' ').replace(/[^a-z0-9\s]/g,' ').split(/\s+/).filter(w=>w.length>1);
  return sw ? w.filter(w=>!STOPWORDS.has(w)) : w;
}

// ── PLUTCHIK 8-EMOTION ENGINE ─────────────────────────────────────────────────
export const EMOTIONS = {
  joy:         { color:'#fbbf24', icon:'😊', words:new Set(['happy','joy','delight','wonderful','fantastic','amazing','love','excited','thrilled','cheerful','glad','pleasure','ecstatic','bliss','great','excellent','perfect','awesome','enjoy','fun','smile','laugh','celebrate','grateful','thankful','appreciate','brilliant','superb']) },
  trust:       { color:'#4ade80', icon:'🤝', words:new Set(['trust','reliable','honest','confident','faithful','secure','safe','dependable','loyal','sincere','believe','certain','solid','proven','consistent','transparent','integrity','credible','authentic','genuine','true']) },
  fear:        { color:'#a78bfa', icon:'😨', words:new Set(['afraid','fear','scared','worried','anxious','nervous','terrified','dread','panic','horror','frightened','concerned','uneasy','threat','danger','risk','warn','warning','alert','dangerous','unsafe','vulnerable','uncertain','doubt']) },
  surprise:    { color:'#22d3ee', icon:'😮', words:new Set(['surprised','shocked','unexpected','sudden','amazing','incredible','unbelievable','astonished','startled','wonder','wow','unexpectedly','suddenly','discovered','realized','turns out']) },
  sadness:     { color:'#60a5fa', icon:'😢', words:new Set(['sad','unhappy','depressed','miserable','gloomy','heartbroken','disappointed','grief','sorrow','regret','upset','unfortunate','terrible','awful','horrible','pain','suffer','loss','miss','lonely','hopeless','despair','cry','tears','hurt','broken','tragic']) },
  disgust:     { color:'#fb923c', icon:'🤢', words:new Set(['disgusting','awful','terrible','horrible','hate','despise','loathe','repulsive','gross','nasty','unacceptable','outrageous','shameful','pathetic','worthless','garbage','trash','junk','useless','broken','defective','worst','avoid']) },
  anger:       { color:'#f87171', icon:'😠', words:new Set(['angry','furious','rage','outraged','frustrated','infuriated','mad','annoyed','irritated','enraged','hostile','aggressive','rude','disrespectful','offensive','scam','fraud','lie','cheat','deceive','mislead','wrong','unfair','unjust','ridiculous','absurd','unacceptable']) },
  anticipation:{ color:'#f472b6', icon:'🔮', words:new Set(['excited','looking','forward','hope','expect','anticipate','await','soon','upcoming','plan','want','need','wish','desire','eager','ready','prepare','next','future','eventually','hopefully','planning','wondering','curious','interested']) },
};

function detectEmotions(text) {
  const words = tok(text, false);
  const scores = {}; let total = 0;
  Object.entries(EMOTIONS).forEach(([name, def]) => { const c = words.filter(w=>def.words.has(w)).length; scores[name]=c; total+=c; });
  const norm = {}; Object.keys(scores).forEach(k=>{ norm[k]=total>0 ? +(scores[k]/total).toFixed(4) : 0; });
  const dominant = Object.entries(scores).sort((a,b)=>b[1]-a[1]);
  return { scores, norm, dominant: dominant[0][1]>0 ? dominant[0][0] : 'neutral', total };
}

// ── INTENT CLASSIFIER ─────────────────────────────────────────────────────────
export const INTENTS = {
  complaint:  { color:C.red,    icon:'⚠',  words:new Set(['broken','doesnt','not working','failed','error','issue','problem','bug','crash','terrible','awful','horrible','worst','never','wasted','refund','return','fix','cant','cannot','stopped','lost','missing','wrong','incorrect','bad','poor','disappointed','frustrated','unacceptable','scam','fraud','defective','damaged','late','delayed','overcharged']) },
  request:    { color:C.cyan,   icon:'💡',  words:new Set(['please','could','would','need','require','request','want','looking','add','feature','support','integrate','allow','enable','option','setting','would love','should have','wish','hope','suggest','idea','improve','enhancement','consider']) },
  praise:     { color:C.green,  icon:'⭐',  words:new Set(['great','excellent','amazing','perfect','love','best','fantastic','wonderful','outstanding','recommend','brilliant','superb','helpful','easy','smooth','fast','reliable','awesome','impressed','exceeded','thank']) },
  question:   { color:C.violet, icon:'❓',  words:new Set(['how','what','when','where','why','which','can','is','does','will','should','do','are','have','any','wondering','anyone','help','clarify','explain','understand','confused','unsure']) },
  comparison: { color:C.gold,   icon:'⚖',  words:new Set(['better','worse','compared','vs','versus','unlike','similar','same','different','switch','switched','preferred','alternative','competitor','replaced','before']) },
  neutral:    { color:C.t3,     icon:'—',   words:new Set([]) },
};

function detectIntent(text) {
  const lower = text.toLowerCase(); const words = tok(lower, false);
  const scores = {};
  Object.entries(INTENTS).forEach(([name, def]) => {
    if (name==='neutral') return;
    let c = words.filter(w=>def.words.has(w)).length;
    if (lower.includes('?')) scores.question = (scores.question||0)+2;
    scores[name] = (scores[name]||0) + c;
  });
  const sorted = Object.entries(scores).sort((a,b)=>b[1]-a[1]);
  return { primary: sorted[0]?.[1]>0 ? sorted[0][0] : 'neutral', secondary: sorted[1]?.[1]>0 ? sorted[1][0] : null, scores };
}

// ── URGENCY / CHURN / ESCALATION SIGNALS ────────────────────────────────────
const URGENCY_W    = new Set(['urgent','immediately','asap','now','critical','emergency','serious','escalate','deadline','today','right now','must','help','broken','cant','cannot','losing','lost','failed','crash','down','outage','blocked','stuck']);
const CHURN_W      = new Set(['cancel','cancelling','cancelled','subscription','leave','leaving','switch','switching','competitor','refund','unsubscribe','quit','done','never again','last time','disappointed','wasted','regret','mistake','moving on','goodbye','bye','deleted']);
const ESCALATION_W = new Set(['manager','supervisor','corporate','legal','lawyer','attorney','sue','lawsuit','complaint','bbb','ftc','report','media','press','twitter','public','expose','unacceptable','outrageous','scam','fraud']);
const SPAM_P       = [/(.)\1{4,}/,/[A-Z]{6,}/,/https?:\/\//,/\b(click|buy|cheap|free|win|offer|limited)\b/i];

function scoreSignals(text) {
  const lower = text.toLowerCase(); const words = tok(lower, false);
  const urg  = words.filter(w=>URGENCY_W.has(w)).length;
  const churn= words.filter(w=>CHURN_W.has(w)).length;
  const esc  = words.filter(w=>ESCALATION_W.has(w)).length;
  const spam = SPAM_P.filter(p=>p.test(text)).length;
  const urgencyScore   = Math.min(10, urg*2   + (lower.includes('!')  ? 1:0));
  const churnRisk      = Math.min(10, churn*2.5);
  const escalationRisk = Math.min(10, esc*3);
  const spamScore      = Math.min(10, spam*3);
  const overallRisk    = Math.min(10, urgencyScore*0.35 + churnRisk*0.35 + escalationRisk*0.3);
  return { urgencyScore, churnRisk, escalationRisk, spamScore, overallRisk:+overallRisk.toFixed(2) };
}

// ── SENTIMENT ────────────────────────────────────────────────────────────────
const SENT_POS = new Set(['good','great','excellent','amazing','wonderful','fantastic','love','best','perfect','beautiful','awesome','outstanding','brilliant','happy','joy','helpful','useful','clear','easy','fast','powerful','effective','nice','enjoy','recommend','impressive','strong','better','benefit','trust','reliable','clean','pleased','excited','grateful','remarkable','ideal','superior']);
const SENT_NEG = new Set(['bad','terrible','awful','horrible','worst','hate','poor','wrong','fail','failure','error','broken','slow','difficult','hard','confusing','useless','worthless','boring','weak','problem','issue','crash','loss','disappoint','pain','toxic','harm','damage','fear','angry','sad','unhappy','impossible','never','worse','expensive','missing','failed','annoying','frustrating','inferior','flawed','defective','corrupt']);
const NEGATORS = new Set(['not','no','never','none','nobody','nothing','neither','nor','hardly','barely','without','lack']);

function sentiment(text) {
  const words = tok(text,false); let score=0, count=0, neg=false;
  words.forEach((w,i)=>{ if(NEGATORS.has(w)){neg=true;return;} const b=i>0&&['very','really','extremely','absolutely','totally'].includes(words[i-1])?1.5:1; if(SENT_POS.has(w)){score+=neg?-b:b;count++;neg=false;} if(SENT_NEG.has(w)){score+=neg?b:-b;count++;neg=false;} });
  const n=count>0?Math.max(-1,Math.min(1,score/(count+Math.sqrt(count)))):0;
  return { score:+n.toFixed(4), label:n>0.15?'positive':n<-0.15?'negative':'neutral', posCount:words.filter(w=>SENT_POS.has(w)).length, negCount:words.filter(w=>SENT_NEG.has(w)).length };
}

// ── TF-IDF + COSINE ───────────────────────────────────────────────────────────
function tfidf(docs) {
  const N=docs.length, df={};
  docs.forEach(t=>{const u=new Set(t); u.forEach(w=>{df[w]=(df[w]||0)+1;});});
  return docs.map(toks=>{ const tf={}; toks.forEach(t=>{tf[t]=(tf[t]||0)+1;}); const out={}; Object.entries(tf).forEach(([t,f])=>{out[t]=+((f/toks.length)*(Math.log((N+1)/((df[t]||0)+1))+1)).toFixed(6);}); return out; });
}

// ── K-MEANS CLUSTERING ────────────────────────────────────────────────────────
function kmeans(vectors, k=5, iters=20) {
  const keys=[...new Set(vectors.flatMap(v=>Object.keys(v)))];
  const toArr=v=>keys.map(k=>v[k]||0);
  const vecs=vectors.map(toArr);
  let centroids=Array.from({length:k},(_,i)=>vecs[Math.floor(i*vecs.length/k)]);
  let labels=new Array(vecs.length).fill(0);
  for(let iter=0;iter<iters;iter++){
    labels=vecs.map(v=>{ let best=0,bestDist=Infinity; centroids.forEach((c,ci)=>{ const dist=c.reduce((s,cv,i)=>s+(cv-v[i])**2,0); if(dist<bestDist){bestDist=dist;best=ci;} }); return best; });
    const newC=Array.from({length:k},()=>new Array(keys.length).fill(0)); const counts=new Array(k).fill(0);
    labels.forEach((l,i)=>{ vecs[i].forEach((v,j)=>{newC[l][j]+=v;}); counts[l]++; });
    centroids=newC.map((c,ci)=>counts[ci]>0?c.map(v=>v/counts[ci]):c);
  }
  return labels;
}

function autoLabel(texts, labels, k) {
  const groups={};
  labels.forEach((l,i)=>{groups[l]=groups[l]||[];groups[l].push(texts[i]);});
  return Object.fromEntries(Object.entries(groups).map(([l,txts])=>{
    const freq={}; txts.forEach(t=>tok(t,true).forEach(w=>{freq[w]=(freq[w]||0)+1;}));
    const top=Object.entries(freq).sort((a,b)=>b[1]-a[1]).slice(0,4).map(([w])=>w).join(', ');
    return [l, top||`Cluster ${+l+1}`];
  }));
}

// ── VOICE OF CUSTOMER EXTRACTION ────────────────────────────────────────────
const PAIN_P  = [/(?:can't|cannot|couldn't|doesn't|won't|isn't|aren't)\s+([\w\s]{2,25})/gi, /(?:problem with|issue with|broken|failed to|error in)\s+(?:the\s+)?([\w\s]{1,25})/gi, /(?:not working|stops working|keeps failing)\s*(?:properly|correctly|at all)?/gi];
const REQ_P   = [/(?:please add|would love|need|want|wish|should have|feature request)[:\s]+([\w\s,]{5,60}?)(?:[.!?\n]|$)/gi, /(?:it would be nice|can you add|could you add)[:\s]?([\w\s,]{5,60}?)(?:[.!?\n]|$)/gi];
const PRAISE_P= [/(?:love the|love how|love that|great|amazing|excellent|fantastic)\s+(the\s+)?([\w\s]{1,25})/gi, /(?:best|favorite|impressed by)\s+(the\s+)?([\w\s]{1,20})/gi];

function extractVoice(texts) {
  const pains={},reqs={},praises={};
  texts.forEach(t=>{
    PAIN_P.forEach(p=>{let m;p.lastIndex=0;while((m=p.exec(t))!==null){const k=(m[1]||m[0]).trim().toLowerCase().slice(0,40);if(k.length>3)pains[k]=(pains[k]||0)+1;}});
    REQ_P.forEach(p=>{let m;p.lastIndex=0;while((m=p.exec(t))!==null){const k=m[1].trim().toLowerCase().slice(0,60);if(k.length>4)reqs[k]=(reqs[k]||0)+1;}});
    PRAISE_P.forEach(p=>{let m;p.lastIndex=0;while((m=p.exec(t))!==null){const k=(m[2]||m[1]||'').trim().toLowerCase().slice(0,40);if(k.length>3)praises[k]=(praises[k]||0)+1;}});
  });
  const sort=obj=>Object.entries(obj).sort((a,b)=>b[1]-a[1]).slice(0,20).map(([text,count])=>({text,count}));
  return { pains:sort(pains), reqs:sort(reqs), praises:sort(praises) };
}

// ── DATE UTILITIES ────────────────────────────────────────────────────────────
function parseDate(str) { if(!str) return null; const d=new Date(str); return isNaN(d)?null:d; }
function bucketByTime(rows, dateCol, valFn, gran='month') {
  const b={};
  rows.forEach(r=>{ const d=parseDate(r[dateCol]); if(!d) return;
    let key; if(gran==='day')key=d.toISOString().slice(0,10); else if(gran==='week'){const w=new Date(d);w.setDate(d.getDate()-d.getDay());key=w.toISOString().slice(0,10);} else key=`${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}`;
    if(!b[key])b[key]=[]; b[key].push(valFn(r));
  });
  return Object.entries(b).sort((a,b)=>a[0].localeCompare(b[0])).map(([period,vals])=>({ period, avg:+(vals.reduce((a,b)=>a+b,0)/vals.length).toFixed(4), count:vals.length, min:+Math.min(...vals).toFixed(4), max:+Math.max(...vals).toFixed(4) }));
}

// ─────────────────────────────────────────────────────────────────────────────
// SHARED UI ATOMS
// ─────────────────────────────────────────────────────────────────────────────
const Card  = ({children,style={}})=><div style={{background:C.surface,border:`1px solid ${C.b1}`,borderRadius:4,overflow:'hidden',...style}}>{children}</div>;
const Head  = ({label,accent=C.violet,right,sub})=>(
  <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',padding:'9px 14px',background:C.deep,borderBottom:`1px solid ${C.b0}`}}>
    <div>
      <div style={{fontSize:9,color:accent,letterSpacing:'.13em',fontWeight:700,fontFamily:'JetBrains Mono,monospace'}}>{label}</div>
      {sub&&<div style={{fontSize:8,color:C.t4,marginTop:1}}>{sub}</div>}
    </div>
    {right}
  </div>
);
const Body  = ({children,style={}})=><div style={{padding:14,...style}}>{children}</div>;
const MB    = ({label,value,color=C.violet,sub})=>(
  <div style={{background:C.raised,border:`1px solid ${color}22`,borderLeft:`2px solid ${color}`,padding:'9px 13px',borderRadius:3}}>
    <div style={{fontSize:8,color:C.t4,fontFamily:'JetBrains Mono,monospace',letterSpacing:'.1em'}}>{label}</div>
    <div style={{fontFamily:'Syne,sans-serif',fontWeight:800,fontSize:18,color,lineHeight:1.1}}>{value}</div>
    {sub&&<div style={{fontSize:9,color:C.t3,marginTop:2}}>{sub}</div>}
  </div>
);
const TH  = {padding:'6px 11px',textAlign:'left',fontSize:9,color:C.t3,letterSpacing:'.09em',borderBottom:`1px solid ${C.b0}`,fontFamily:'JetBrains Mono,monospace',whiteSpace:'nowrap'};
const TD  = {padding:'6px 11px',color:'#b6c3d8',fontFamily:'JetBrains Mono,monospace',fontSize:10};
const MBar= ({pct,color=C.violet,h=4})=><div style={{width:'100%',height:h,background:C.b1,borderRadius:2}}><div style={{height:'100%',width:`${Math.min(pct,100)}%`,background:color,borderRadius:2}}/></div>;
const Info = ({children,color=C.violet})=><div style={{padding:'10px 13px',background:color+'10',border:`1px solid ${color}33`,borderRadius:3,fontSize:11,color:C.t2,lineHeight:1.7,marginTop:8}}>{children}</div>;
const sentC= s=>s==='positive'?C.green:s==='negative'?C.red:C.t3;
const intC = i=>INTENTS[i]?.color||C.t3;
const Badge= ({label,color})=><span style={{fontSize:8,padding:'2px 7px',border:`1px solid ${color}55`,color,borderRadius:2,fontFamily:'JetBrains Mono,monospace',fontWeight:700,whiteSpace:'nowrap'}}>{label}</span>;
const RiskBar=({label,value,max=10,color})=>(
  <div style={{marginBottom:8}}>
    <div style={{display:'flex',justifyContent:'space-between',marginBottom:3}}><span style={{fontSize:9,color:C.t3,fontFamily:'JetBrains Mono,monospace'}}>{label}</span><span style={{fontSize:9,color,fontWeight:700,fontFamily:'JetBrains Mono,monospace'}}>{value}/10</span></div>
    <div style={{height:6,background:C.b1,borderRadius:3}}><div style={{height:'100%',width:`${value/max*100}%`,background:color,borderRadius:3}}/></div>
  </div>
);

// ─────────────────────────────────────────────────────────────────────────────
// SETUP TAB
// ─────────────────────────────────────────────────────────────────────────────
function DeepSetupTab({ ds, config, onConfigChange }) {
  const types = useMemo(()=>inferTypes(ds.headers,ds.rows),[ds]);
  const textCols= ds.headers.filter(h=>{const t=types.find(t=>t.col===h);if(t?.type!=='categorical')return false;const vals=ds.rows.map(r=>String(r[h]||'')).filter(Boolean);return vals.reduce((s,v)=>s+v.length,0)/Math.max(vals.length,1)>15;});
  const catCols = ds.headers.filter(h=>types.find(t=>t.col===h)?.type==='categorical');
  const dateCols= ds.headers.filter(h=>{const vals=ds.rows.slice(0,20).map(r=>r[h]).filter(Boolean);return vals.filter(v=>!isNaN(new Date(v))).length>vals.length*0.6;});
  const { primaryCol='', groupCol='', dateCol='', sampleSize=1000, useStopwords=true } = config;

  return (
    <div style={{display:'flex',flexDirection:'column',gap:14}}>
      <Card>
        <Head label="COLUMN PROFILER" accent={C.violet} sub={`${textCols.length} text · ${catCols.length} categorical · ${dateCols.length} date-like`}/>
        <div style={{overflowX:'auto'}}>
          <table style={{width:'100%',borderCollapse:'collapse'}}>
            <thead><tr style={{background:C.deep}}>{['COLUMN','TYPE','AVG CHARS','UNIQUE','NULLS','ROLE'].map(h=><th key={h} style={TH}>{h}</th>)}</tr></thead>
            <tbody>
              {ds.headers.map(h=>{
                const t=types.find(t=>t.col===h);
                const vals=ds.rows.map(r=>String(r[h]||'')).filter(Boolean);
                const avg=+(vals.reduce((s,v)=>s+v.length,0)/Math.max(vals.length,1)).toFixed(0);
                const role=h===primaryCol?'PRIMARY TEXT':h===groupCol?'GROUP BY':h===dateCol?'DATE':textCols.includes(h)?'text-like':dateCols.includes(h)?'date-like':t?.type;
                const roleC=h===primaryCol?C.violet:h===groupCol?C.gold:h===dateCol?C.cyan:C.t4;
                return(
                  <tr key={h} style={{borderBottom:`1px solid ${C.b0}`,background:h===primaryCol?C.violet+'08':'transparent'}}>
                    <td style={{...TD,color:h===primaryCol?C.violet:C.t2,fontWeight:h===primaryCol?700:400,borderLeft:`2px solid ${h===primaryCol?C.violet:C.b1}`}}>{h}</td>
                    <td style={{...TD}}><span style={{fontSize:8,padding:'1px 6px',borderRadius:2,background:(t?.type==='numeric'?C.cyan:C.gold)+'18',color:t?.type==='numeric'?C.cyan:C.gold}}>{t?.type}</span></td>
                    <td style={{...TD,color:C.t2}}>{avg}</td>
                    <td style={{...TD}}>{t?.uniqueCount}</td>
                    <td style={{...TD,color:t?.nullCount>0?C.red:C.t4}}>{t?.nullCount}</td>
                    <td style={{...TD,color:roleC,fontWeight:700}}>{role}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </Card>

      <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:14}}>
        <Card>
          <Head label="DEEP ANALYSIS CONFIGURATION" accent={C.violet}/>
          <Body>
            <div style={{display:'flex',flexDirection:'column',gap:12}}>
              <div>
                <Label>PRIMARY TEXT COLUMN *</Label>
                <select value={primaryCol} onChange={e=>onConfigChange({primaryCol:e.target.value})} style={{width:'100%',background:C.deep,border:`2px solid ${C.violet}`,color:C.t1,padding:'8px 12px',fontSize:12,fontFamily:'JetBrains Mono,monospace',borderRadius:3,outline:'none'}}>
                  <option value="">— select —</option>
                  {ds.headers.map(h=><option key={h} value={h}>{h}</option>)}
                </select>
              </div>
              <div>
                <Label>GROUP / CATEGORY COLUMN — powers A/B compare and group breakdowns</Label>
                <select value={groupCol} onChange={e=>onConfigChange({groupCol:e.target.value})} style={{width:'100%',background:C.deep,border:`1px solid ${C.b2}`,color:C.t1,padding:'7px 10px',fontSize:11,fontFamily:'JetBrains Mono,monospace',borderRadius:3,outline:'none'}}>
                  <option value="">— none —</option>
                  {catCols.map(h=><option key={h} value={h}>{h}</option>)}
                </select>
              </div>
              <div>
                <Label>DATE COLUMN — enables TRENDS temporal analysis</Label>
                <select value={dateCol} onChange={e=>onConfigChange({dateCol:e.target.value})} style={{width:'100%',background:C.deep,border:`1px solid ${dateCols.length>0?C.cyan:C.b2}`,color:C.t1,padding:'7px 10px',fontSize:11,fontFamily:'JetBrains Mono,monospace',borderRadius:3,outline:'none'}}>
                  <option value="">— none —</option>
                  {ds.headers.map(h=><option key={h} value={h}>{h}</option>)}
                </select>
              </div>
              <label style={{display:'flex',alignItems:'center',gap:8,cursor:'pointer'}}>
                <input type="checkbox" checked={useStopwords} onChange={e=>onConfigChange({useStopwords:e.target.checked})} style={{accentColor:C.violet,width:14,height:14}}/>
                <span style={{fontSize:11,color:C.t2}}>Remove stopwords for clustering & keywords</span>
              </label>
              <div>
                <Label>SAMPLE SIZE</Label>
                <div style={{display:'flex',gap:6}}>
                  {[500,1000,2000,5000,'ALL'].map(n=>(
                    <button key={n} onClick={()=>onConfigChange({sampleSize:n==='ALL'?999999:n})} style={{padding:'5px 10px',border:`1px solid ${sampleSize===(n==='ALL'?999999:n)?C.violet:C.b1}`,background:sampleSize===(n==='ALL'?999999:n)?C.violet+'18':'transparent',color:sampleSize===(n==='ALL'?999999:n)?C.violet:C.t3,fontSize:9,borderRadius:3,cursor:'pointer',fontFamily:'JetBrains Mono,monospace'}}>{n}</button>
                  ))}
                </div>
              </div>
            </div>
          </Body>
        </Card>
        <Card>
          <Head label="WHAT EACH TAB UNLOCKS" accent={C.violet}/>
          <Body>
            {[
              ['🎭','EMOTION',    C.gold,   'Plutchik 8-emotion wheel — Joy · Trust · Fear · Surprise · Sadness · Disgust · Anger · Anticipation. Per-doc and corpus-wide.'],
              ['🎯','INTENT',    C.cyan,   'Classifies each doc: Complaint · Request · Praise · Question · Comparison. Surface product pain + opportunity.'],
              ['🔵','CLUSTERS',  C.violet, 'K-means on TF-IDF. Auto-labels each cluster. Find natural doc segments without manual tagging.'],
              ['📈','TRENDS',    C.green,  'Sentiment + emotion + volume over time. Requires DATE column. Spot narrative shifts and seasonal patterns.'],
              ['🚨','SIGNALS',   C.red,    'Urgency · Churn Risk · Escalation Risk per doc. Surface the highest-risk documents instantly.'],
              ['💬','VOICE',     C.orange, 'Pain points · Feature requests · Praise themes via pattern matching. Direct voice-of-customer.'],
              ['⚖','A/B COMPARE',C.gold,  'Split by any category. Compare sentiment, emotion, intent, vocabulary between groups.'],
              ['🏷','ANNOTATE',  C.pink,   'Manual label + auto-enrichment (sentiment, intent, emotion). Export as labeled CSV for ML training.'],
            ].map(([ic,tab,color,desc])=>(
              <div key={tab} style={{display:'flex',gap:10,padding:'7px 0',borderBottom:`1px solid ${C.b0}`}}>
                <span style={{fontSize:16,flexShrink:0}}>{ic}</span>
                <div><div style={{fontSize:9,color,fontFamily:'JetBrains Mono,monospace',fontWeight:700}}>{tab}</div><div style={{fontSize:10,color:C.t3,lineHeight:1.6}}>{desc}</div></div>
              </div>
            ))}
          </Body>
        </Card>
      </div>

      {primaryCol&&(
        <Card>
          <Head label="LIVE SIGNAL PREVIEW — FIRST 5 DOCUMENTS" accent={C.violet}/>
          <div style={{overflowX:'auto'}}>
            <table style={{width:'100%',borderCollapse:'collapse',fontSize:11}}>
              <thead><tr style={{background:C.deep}}>{['#','TEXT','SENTIMENT','EMOTION','INTENT','URGENCY','CHURN'].map(h=><th key={h} style={TH}>{h}</th>)}</tr></thead>
              <tbody>
                {ds.rows.slice(0,5).map((r,i)=>{
                  const text=String(r[primaryCol]||'');
                  const s=sentiment(text),e=detectEmotions(text),it=detectIntent(text),sig=scoreSignals(text);
                  return(
                    <tr key={i} style={{borderBottom:`1px solid ${C.b0}`}}>
                      <td style={{...TD,color:C.t4}}>{i+1}</td>
                      <td style={{...TD,color:C.t2,maxWidth:240}}><div style={{overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{text.slice(0,100)}</div></td>
                      <td style={{...TD}}><Badge label={s.label.toUpperCase()} color={sentC(s.label)}/></td>
                      <td style={{...TD}}><Badge label={`${EMOTIONS[e.dominant]?.icon||'—'} ${e.dominant}`} color={EMOTIONS[e.dominant]?.color||C.t3}/></td>
                      <td style={{...TD}}><Badge label={`${INTENTS[it.primary]?.icon||''} ${it.primary}`} color={intC(it.primary)}/></td>
                      <td style={{...TD,color:sig.urgencyScore>5?C.red:C.t3,fontWeight:700}}>{sig.urgencyScore}/10</td>
                      <td style={{...TD,color:sig.churnRisk>5?C.red:C.t3,fontWeight:700}}>{sig.churnRisk.toFixed(1)}/10</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </Card>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// EMOTION TAB
// ─────────────────────────────────────────────────────────────────────────────
function EmotionTab({ texts, rows, col, config }) {
  const [view,setView]=useState('corpus');
  const scored=useMemo(()=>texts.map((t,i)=>({idx:i,text:t,...detectEmotions(t)})),[texts]);
  const corpusScores=useMemo(()=>{
    const agg={}; Object.keys(EMOTIONS).forEach(k=>{agg[k]=0;});
    scored.forEach(s=>Object.entries(s.scores).forEach(([k,v])=>{agg[k]+=v;}));
    const total=Object.values(agg).reduce((a,b)=>a+b,1);
    return Object.entries(agg).map(([name,count])=>({name,count,pct:+(count/total*100).toFixed(1),...EMOTIONS[name]})).sort((a,b)=>b.count-a.count);
  },[scored]);
  const radarData=useMemo(()=>Object.keys(EMOTIONS).map(k=>({emotion:k,score:+(scored.reduce((a,s)=>a+(s.norm[k]||0),0)/Math.max(scored.length,1)*100).toFixed(1)})),[scored]);
  const domDist=useMemo(()=>{
    const d={}; Object.keys(EMOTIONS).forEach(k=>{d[k]=0;}); d.neutral=0;
    scored.forEach(s=>{d[s.dominant]=(d[s.dominant]||0)+1;});
    return Object.entries(d).filter(([,v])=>v>0).sort((a,b)=>b[1]-a[1]).map(([name,count])=>({name,count,icon:EMOTIONS[name]?.icon||'—',color:EMOTIONS[name]?.color||C.t3}));
  },[scored]);
  const groupEm=useMemo(()=>{
    if(!config.groupCol)return[];
    const groups={};
    rows.slice(0,texts.length).forEach((r,i)=>{const g=r[config.groupCol]||'—';if(!groups[g])groups[g]={label:g,scores:{}};Object.keys(EMOTIONS).forEach(k=>{groups[g].scores[k]=(groups[g].scores[k]||0)+(scored[i]?.scores[k]||0);});});
    return Object.values(groups).map(g=>{const total=Object.values(g.scores).reduce((a,b)=>a+b,1);const norm={};Object.entries(g.scores).forEach(([k,v])=>{norm[k]=+(v/total*100).toFixed(1);});return{label:g.label,...norm};}).slice(0,10);
  },[rows,texts,scored,config]);

  return(
    <div style={{display:'flex',flexDirection:'column',gap:14}}>
      <div style={{display:'flex',gap:4}}>
        {[['corpus','CORPUS'],['docs','PER DOC'],['groups','BY GROUP']].map(([v,l])=>(
          <button key={v} onClick={()=>setView(v)} style={{padding:'7px 14px',border:`1px solid ${view===v?C.gold:C.b0}`,background:view===v?C.gold+'15':C.raised,color:view===v?C.gold:C.t2,fontSize:10,cursor:'pointer',borderRadius:3,fontFamily:'JetBrains Mono,monospace',fontWeight:view===v?700:400}}>{l}</button>
        ))}
      </div>

      {view==='corpus'&&(
        <div style={{display:'flex',flexDirection:'column',gap:14}}>
          <div style={{display:'grid',gridTemplateColumns:'repeat(4,1fr)',gap:10}}>
            {corpusScores.slice(0,4).map(e=><MB key={e.name} label={`${e.icon} ${e.name.toUpperCase()}`} value={`${e.pct}%`} color={e.color} sub={`${e.count} signals`}/>)}
          </div>
          <div style={{display:'grid',gridTemplateColumns:'repeat(4,1fr)',gap:10}}>
            {corpusScores.slice(4).map(e=><MB key={e.name} label={`${e.icon} ${e.name.toUpperCase()}`} value={`${e.pct}%`} color={e.color} sub={`${e.count} signals`}/>)}
          </div>
          <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:14}}>
            <Card>
              <Head label="EMOTION RADAR — CORPUS AVERAGE" accent={C.gold} right={<PinButton chart={{id:'deep_emradar',type:'radar',title:'Emotion Radar'}} small/>}/>
              <Body>
                <ResponsiveContainer width="100%" height={300}>
                  <RadarChart data={radarData}>
                    <PolarGrid stroke={C.b1}/><PolarAngleAxis dataKey="emotion" tick={{fill:C.t3,fontSize:10}}/><PolarRadiusAxis angle={30} domain={[0,100]} tick={{fill:C.t4,fontSize:8}}/>
                    <Radar dataKey="score" stroke={C.gold} fill={C.gold} fillOpacity={0.3}/><Tooltip content={<ChartTip/>}/>
                  </RadarChart>
                </ResponsiveContainer>
              </Body>
            </Card>
            <Card>
              <Head label="DOMINANT EMOTION DISTRIBUTION" accent={C.gold} right={<PinButton chart={{id:'deep_emdist',type:'bar',title:'Dominant Emotions'}} small/>}/>
              <Body>
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={domDist} layout="vertical" barCategoryGap="18%" margin={{top:0,right:80,bottom:0,left:0}}>
                    <CartesianGrid strokeDasharray="2 2" stroke={C.b0} horizontal={false}/><XAxis type="number" tick={{fill:C.t4,fontSize:9}}/><YAxis dataKey="name" type="category" tick={{fill:C.t2,fontSize:10}} width={100}/>
                    <Tooltip content={<ChartTip/>}/>
                    <Bar dataKey="count" name="documents" radius={[0,3,3,0]}>{domDist.map(d=><Cell key={d.name} fill={d.color}/>)}</Bar>
                  </BarChart>
                </ResponsiveContainer>
              </Body>
            </Card>
          </div>
          <Card>
            <Head label="EMOTION INTENSITY — ALL 8 EMOTIONS" accent={C.gold} right={<PinButton chart={{id:'deep_embar',type:'bar',title:'Emotion Intensity'}} small/>}/>
            <Body>
              <ResponsiveContainer width="100%" height={180}>
                <BarChart data={corpusScores} barCategoryGap="20%">
                  <CartesianGrid strokeDasharray="2 2" stroke={C.b0}/><XAxis dataKey="name" tick={{fill:C.t3,fontSize:10}}/><YAxis tick={{fill:C.t4,fontSize:8}}/><Tooltip content={<ChartTip/>}/>
                  <Bar dataKey="count" name="signal count" radius={[3,3,0,0]}>{corpusScores.map(e=><Cell key={e.name} fill={e.color}/>)}</Bar>
                </BarChart>
              </ResponsiveContainer>
            </Body>
          </Card>
        </div>
      )}

      {view==='docs'&&(
        <Card>
          <Head label={`PER-DOCUMENT EMOTION — ${scored.length} docs`} accent={C.gold}/>
          <div style={{maxHeight:600,overflowY:'auto'}}>
            <table style={{width:'100%',borderCollapse:'collapse',fontSize:11}}>
              <thead style={{position:'sticky',top:0,zIndex:1}}><tr style={{background:C.deep}}>
                <th style={TH}>#</th><th style={TH}>TEXT</th><th style={TH}>DOMINANT</th>
                {Object.keys(EMOTIONS).map(k=><th key={k} style={{...TH,color:EMOTIONS[k].color}}>{EMOTIONS[k].icon}</th>)}
              </tr></thead>
              <tbody>
                {scored.slice(0,150).map((s,i)=>(
                  <tr key={s.idx} style={{borderBottom:`1px solid ${C.b0}`,background:i%2===0?'transparent':C.raised+'60'}}>
                    <td style={{...TD,color:C.t4}}>{i+1}</td>
                    <td style={{...TD,color:C.t2,maxWidth:260}}><div style={{overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{s.text.slice(0,100)}</div></td>
                    <td style={{...TD}}><Badge label={`${EMOTIONS[s.dominant]?.icon||'—'} ${s.dominant}`} color={EMOTIONS[s.dominant]?.color||C.t3}/></td>
                    {Object.keys(EMOTIONS).map(k=><td key={k} style={{...TD,color:s.scores[k]>0?EMOTIONS[k].color:C.t4}}>{s.scores[k]>0?s.scores[k]:'—'}</td>)}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {view==='groups'&&(
        groupEm.length>0?(
          <Card>
            <Head label={`EMOTION BY GROUP — ${config.groupCol}`} accent={C.gold} right={<PinButton chart={{id:'deep_emgroup',type:'bar',title:`Emotion by ${config.groupCol}`}} small/>}/>
            <Body>
              <ResponsiveContainer width="100%" height={Math.max(260,groupEm.length*36+60)}>
                <BarChart data={groupEm} layout="vertical" barCategoryGap="10%">
                  <CartesianGrid strokeDasharray="2 2" stroke={C.b0} horizontal={false}/><XAxis type="number" tick={{fill:C.t4,fontSize:8}}/><YAxis dataKey="label" type="category" tick={{fill:C.t2,fontSize:9}} width={110}/>
                  <Tooltip content={<ChartTip/>}/><Legend wrapperStyle={{fontSize:9}}/>
                  {Object.entries(EMOTIONS).map(([k,e])=><Bar key={k} dataKey={k} stackId="a" fill={e.color} name={`${e.icon} ${k}`}/>)}
                </BarChart>
              </ResponsiveContainer>
            </Body>
          </Card>
        ):<Info color={C.gold}>Set a GROUP column in SETUP to compare emotions across segments.</Info>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// INTENT TAB
// ─────────────────────────────────────────────────────────────────────────────
function IntentTab({ texts, rows, col, config }) {
  const [filter,setFilter]=useState('all');
  const [search,setSearch]=useState('');
  const scored=useMemo(()=>texts.map((t,i)=>({idx:i,text:t,...detectIntent(t)})),[texts]);
  const summary=useMemo(()=>{const c={};Object.keys(INTENTS).forEach(k=>{c[k]=0;});scored.forEach(s=>{c[s.primary]=(c[s.primary]||0)+1;});return c;},[scored]);
  const pieData=Object.entries(summary).filter(([,v])=>v>0).sort((a,b)=>b[1]-a[1]).map(([name,value])=>({name,value,color:INTENTS[name]?.color||C.t3}));
  const filtered=useMemo(()=>{let s=filter!=='all'?scored.filter(x=>x.primary===filter):scored;if(search.trim())s=s.filter(x=>x.text.toLowerCase().includes(search.toLowerCase()));return s;},[scored,filter,search]);
  const groupBreak=useMemo(()=>{
    if(!config.groupCol)return[];
    const groups={};
    rows.slice(0,texts.length).forEach((r,i)=>{const g=r[config.groupCol]||'—';if(!groups[g])groups[g]={label:g};Object.keys(INTENTS).forEach(k=>{groups[g][k]=(groups[g][k]||0)+(scored[i]?.primary===k?1:0);});});
    return Object.values(groups).slice(0,10);
  },[rows,texts,scored,config]);

  return(
    <div style={{display:'flex',flexDirection:'column',gap:14}}>
      <div style={{display:'grid',gridTemplateColumns:'repeat(5,1fr)',gap:10}}>
        {Object.entries(INTENTS).filter(([k])=>k!=='neutral').map(([name,def])=>(
          <MB key={name} label={`${def.icon} ${name.toUpperCase()}`} value={summary[name]||0} color={def.color} sub={`${((summary[name]||0)/scored.length*100).toFixed(1)}%`}/>
        ))}
      </div>
      <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:14}}>
        <Card>
          <Head label="INTENT DISTRIBUTION" accent={C.cyan} right={<PinButton chart={{id:'deep_intent_pie',type:'pie',title:'Intent Distribution'}} small/>}/>
          <Body>
            <ResponsiveContainer width="100%" height={220}>
              <PieChart>
                <Pie data={pieData} dataKey="value" cx="50%" cy="50%" outerRadius={80} paddingAngle={3} label={({name,percent})=>`${INTENTS[name]?.icon} ${name} ${(percent*100).toFixed(0)}%`} labelLine={false}>
                  {pieData.map((d,i)=><Cell key={i} fill={d.color}/>)}
                </Pie><Tooltip content={<ChartTip/>}/>
              </PieChart>
            </ResponsiveContainer>
          </Body>
        </Card>
        {groupBreak.length>0&&(
          <Card>
            <Head label={`INTENT BY GROUP — ${config.groupCol}`} accent={C.cyan} right={<PinButton chart={{id:'deep_intent_group',type:'bar',title:`Intent by ${config.groupCol}`}} small/>}/>
            <Body>
              <ResponsiveContainer width="100%" height={220}>
                <BarChart data={groupBreak} layout="vertical" barCategoryGap="10%">
                  <CartesianGrid strokeDasharray="2 2" stroke={C.b0} horizontal={false}/><XAxis type="number" tick={{fill:C.t4,fontSize:8}}/><YAxis dataKey="label" type="category" tick={{fill:C.t2,fontSize:9}} width={110}/>
                  <Tooltip content={<ChartTip/>}/>
                  {Object.entries(INTENTS).filter(([k])=>k!=='neutral').map(([k,d])=><Bar key={k} dataKey={k} stackId="a" fill={d.color} name={`${d.icon} ${k}`}/>)}
                </BarChart>
              </ResponsiveContainer>
            </Body>
          </Card>
        )}
      </div>
      {summary.complaint>summary.praise&&<Info color={C.red}>⚠ Complaints ({summary.complaint}) outweigh praise ({summary.praise||0}). High ratio signals product/service issues requiring immediate attention.</Info>}
      {summary.request>0&&<Info color={C.cyan}>💡 {summary.request} feature requests detected — check the VOICE tab for extracted themes.</Info>}
      <Card>
        <Head label={`PER-DOCUMENT INTENT — ${filtered.length} shown`} accent={C.cyan}/>
        <Body style={{paddingBottom:0}}>
          <div style={{display:'flex',gap:8,marginBottom:10,flexWrap:'wrap',alignItems:'center'}}>
            <input value={search} onChange={e=>setSearch(e.target.value)} placeholder="Search…" style={{flex:1,minWidth:160,background:C.deep,border:`1px solid ${C.b2}`,color:C.t1,padding:'6px 10px',fontSize:11,fontFamily:'JetBrains Mono,monospace',borderRadius:3,outline:'none'}}/>
            {['all',...Object.keys(INTENTS)].map(f=>(
              <button key={f} onClick={()=>setFilter(f)} style={{padding:'4px 9px',border:`1px solid ${filter===f?(INTENTS[f]?.color||C.violet):C.b1}`,background:filter===f?(INTENTS[f]?.color||C.violet)+'18':'transparent',color:filter===f?(INTENTS[f]?.color||C.violet):C.t3,fontSize:8,borderRadius:2,cursor:'pointer',fontFamily:'JetBrains Mono,monospace',fontWeight:700}}>{f.toUpperCase()}</button>
            ))}
          </div>
        </Body>
        <div style={{maxHeight:440,overflowY:'auto'}}>
          <table style={{width:'100%',borderCollapse:'collapse',fontSize:11}}>
            <thead style={{position:'sticky',top:0,zIndex:1}}><tr style={{background:C.deep}}>{['#','TEXT PREVIEW','PRIMARY','SECONDARY','CONFIDENCE'].map(h=><th key={h} style={TH}>{h}</th>)}</tr></thead>
            <tbody>
              {filtered.slice(0,120).map((s,i)=>(
                <tr key={s.idx} style={{borderBottom:`1px solid ${C.b0}`,background:i%2===0?'transparent':C.raised+'60'}}>
                  <td style={{...TD,color:C.t4}}>{s.idx+1}</td>
                  <td style={{...TD,color:C.t2,maxWidth:320}}><div style={{overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{s.text.slice(0,130)}</div></td>
                  <td style={{...TD}}><Badge label={`${INTENTS[s.primary]?.icon||''} ${s.primary.toUpperCase()}`} color={intC(s.primary)}/></td>
                  <td style={{...TD}}>{s.secondary&&<Badge label={s.secondary} color={intC(s.secondary)}/>}</td>
                  <td style={{...TD,color:C.t3,fontSize:9}}>{Object.entries(s.scores).filter(([,v])=>v>0).map(([k,v])=>`${k}:${v}`).join(' · ')}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// CLUSTERS TAB
// ─────────────────────────────────────────────────────────────────────────────
function ClustersTab({ texts, col, config }) {
  const [k,setK]=useState(5);
  const [clusters,setClusters]=useState(null);
  const [running,setRunning]=useState(false);

  const run=useCallback(async()=>{
    setRunning(true);
    await new Promise(r=>setTimeout(r,30));
    const sample=texts.slice(0,800);
    const toks=sample.map(t=>tok(t,true).filter(w=>w.length>2));
    const vecs=tfidf(toks);
    const labels=kmeans(vecs,k);
    const names=autoLabel(sample,labels,k);
    const groups={};
    labels.forEach((l,i)=>{ if(!groups[l])groups[l]={id:l,label:names[l]||`Cluster ${l+1}`,docs:[],color:PALETTE[l%PALETTE.length]}; groups[l].docs.push({idx:i,text:sample[i]}); });
    Object.values(groups).forEach(g=>{
      const sents=g.docs.map(d=>sentiment(d.text));
      g.avgSentiment=+(sents.reduce((a,s)=>a+s.score,0)/sents.length).toFixed(4);
      g.posCount=sents.filter(s=>s.label==='positive').length;
      g.negCount=sents.filter(s=>s.label==='negative').length;
      g.count=g.docs.length;
      const freq={}; g.docs.forEach(d=>tok(d.text,true).forEach(w=>{freq[w]=(freq[w]||0)+1;}));
      g.topTerms=Object.entries(freq).sort((a,b)=>b[1]-a[1]).slice(0,12).map(([w,c])=>({word:w,count:c}));
      const ems=g.docs.map(d=>detectEmotions(d.text));
      const emAgg={}; Object.keys(EMOTIONS).forEach(em=>{emAgg[em]=0;});
      ems.forEach(e=>Object.entries(e.scores).forEach(([k,v])=>{emAgg[k]+=v;}));
      const emTotal=Object.values(emAgg).reduce((a,b)=>a+b,1);
      g.dominantEmotion=Object.entries(emAgg).sort((a,b)=>b[1]-a[1])[0][0];
      const ints=g.docs.map(d=>detectIntent(d.text));
      const intCounts={}; Object.keys(INTENTS).forEach(it=>{intCounts[it]=0;});
      ints.forEach(i=>{intCounts[i.primary]=(intCounts[i.primary]||0)+1;});
      g.dominantIntent=Object.entries(intCounts).sort((a,b)=>b[1]-a[1])[0][0];
      g.intCounts=intCounts;
    });
    setClusters(Object.values(groups).sort((a,b)=>b.count-a.count));
    setRunning(false);
  },[texts,k]);

  const autoRan=useRef(false);
  useEffect(()=>{if(texts.length>0&&!autoRan.current){autoRan.current=true;run();}},[]);//eslint-disable-line

  const chartData=clusters?.map(c=>({label:`C${c.id+1}`,labelFull:c.label.slice(0,18),count:c.count,sentiment:c.avgSentiment,color:c.color}));

  return(
    <div style={{display:'flex',flexDirection:'column',gap:14}}>
      <Card>
        <Head label="K-MEANS CLUSTERING — PURE-JS TF-IDF VECTORS" accent={C.violet}/>
        <Body>
          <div style={{display:'flex',gap:14,alignItems:'flex-end',flexWrap:'wrap'}}>
            <div>
              <Label>NUMBER OF CLUSTERS (k)</Label>
              <div style={{display:'flex',gap:6}}>
                {[3,4,5,6,7,8,10].map(n=>(
                  <button key={n} onClick={()=>setK(n)} style={{padding:'5px 10px',border:`1px solid ${k===n?C.violet:C.b1}`,background:k===n?C.violet+'18':'transparent',color:k===n?C.violet:C.t3,fontSize:10,borderRadius:3,cursor:'pointer',fontFamily:'JetBrains Mono,monospace',fontWeight:700}}>{n}</button>
                ))}
              </div>
            </div>
            <Btn onClick={run} color={C.violet} disabled={running}>{running?'⟳ CLUSTERING…':'▶ RUN CLUSTERING'}</Btn>
            {clusters&&<span style={{fontSize:10,color:C.t3}}>✓ {Math.min(texts.length,800)} docs → {k} clusters</span>}
          </div>
          <Info color={C.violet}>K-means on TF-IDF vectors. Auto-labeled by top co-occurring terms. Each cluster also gets sentiment, dominant emotion, and dominant intent — no manual tagging needed.</Info>
        </Body>
      </Card>

      {clusters&&(
        <>
          <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(300px,1fr))',gap:12}}>
            {clusters.map(c=>(
              <Card key={c.id}>
                <Head label={`CLUSTER ${c.id+1} · ${c.count} DOCS`} accent={c.color} sub={`${(c.count/Math.min(texts.length,800)*100).toFixed(1)}% of corpus`}/>
                <Body>
                  <div style={{display:'flex',flexWrap:'wrap',gap:6,marginBottom:10}}>
                    {c.topTerms.slice(0,10).map((t,i)=>(
                      <span key={t.word} style={{padding:'3px 9px',background:c.color+'15',border:`1px solid ${c.color}44`,borderRadius:12,fontSize:i===0?12:10,color:c.color,fontWeight:i<3?700:400}}>{i===0&&'★ '}{t.word}</span>
                    ))}
                  </div>
                  <MBar pct={c.count/clusters[0].count*100} color={c.color} h={5}/>
                  <div style={{display:'grid',gridTemplateColumns:'1fr 1fr 1fr',gap:6,marginTop:10}}>
                    <div style={{textAlign:'center',padding:'5px',background:C.deep,borderRadius:3}}>
                      <div style={{fontSize:8,color:C.t4}}>SENTIMENT</div>
                      <div style={{fontFamily:'Syne,sans-serif',fontWeight:800,fontSize:13,color:sentC(c.avgSentiment>0.1?'positive':c.avgSentiment<-0.1?'negative':'neutral')}}>{c.avgSentiment}</div>
                    </div>
                    <div style={{textAlign:'center',padding:'5px',background:C.deep,borderRadius:3}}>
                      <div style={{fontSize:8,color:C.t4}}>EMOTION</div>
                      <div style={{fontSize:12,color:EMOTIONS[c.dominantEmotion]?.color||C.t3}}>{EMOTIONS[c.dominantEmotion]?.icon} {c.dominantEmotion}</div>
                    </div>
                    <div style={{textAlign:'center',padding:'5px',background:C.deep,borderRadius:3}}>
                      <div style={{fontSize:8,color:C.t4}}>INTENT</div>
                      <div style={{fontSize:11,color:INTENTS[c.dominantIntent]?.color||C.t3}}>{INTENTS[c.dominantIntent]?.icon} {c.dominantIntent}</div>
                    </div>
                  </div>
                  <div style={{marginTop:8,padding:'6px 10px',background:C.deep,borderRadius:3,borderLeft:`2px solid ${c.color}`}}>
                    <div style={{fontSize:9,color:C.t4,marginBottom:2}}>SAMPLE</div>
                    <div style={{fontSize:10,color:C.t2}}>{c.docs[0]?.text.slice(0,110)}…</div>
                  </div>
                </Body>
              </Card>
            ))}
          </div>

          <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:14}}>
            <Card>
              <Head label="CLUSTER SIZE" accent={C.violet} right={<PinButton chart={{id:'deep_cl_size',type:'bar',title:'Cluster Size'}} small/>}/>
              <Body>
                <ResponsiveContainer width="100%" height={200}>
                  <BarChart data={chartData} barCategoryGap="15%">
                    <CartesianGrid strokeDasharray="2 2" stroke={C.b0}/><XAxis dataKey="labelFull" tick={{fill:C.t4,fontSize:8}}/><YAxis tick={{fill:C.t4,fontSize:8}}/><Tooltip content={<ChartTip/>}/>
                    <Bar dataKey="count" name="documents" radius={[3,3,0,0]}>{chartData.map((d,i)=><Cell key={i} fill={d.color}/>)}</Bar>
                  </BarChart>
                </ResponsiveContainer>
              </Body>
            </Card>
            <Card>
              <Head label="CLUSTER AVG SENTIMENT" accent={C.violet} right={<PinButton chart={{id:'deep_cl_sent',type:'bar',title:'Cluster Sentiment'}} small/>}/>
              <Body>
                <ResponsiveContainer width="100%" height={200}>
                  <BarChart data={chartData} barCategoryGap="15%">
                    <CartesianGrid strokeDasharray="2 2" stroke={C.b0}/><XAxis dataKey="labelFull" tick={{fill:C.t4,fontSize:8}}/><YAxis domain={[-1,1]} tick={{fill:C.t4,fontSize:8}}/><Tooltip content={<ChartTip/>}/>
                    <Bar dataKey="sentiment" name="avg sentiment" radius={[3,3,0,0]}>{chartData.map((d,i)=><Cell key={i} fill={d.sentiment>0?C.green:C.red}/>)}</Bar>
                  </BarChart>
                </ResponsiveContainer>
              </Body>
            </Card>
          </div>

          <Card>
            <Head label="CLUSTER INTENT BREAKDOWN" accent={C.violet} right={<PinButton chart={{id:'deep_cl_intent',type:'bar',title:'Cluster Intent Breakdown'}} small/>}/>
            <Body>
              <ResponsiveContainer width="100%" height={200}>
                <BarChart data={clusters.map(c=>({label:`C${c.id+1}`, ...c.intCounts}))} barCategoryGap="10%">
                  <CartesianGrid strokeDasharray="2 2" stroke={C.b0}/><XAxis dataKey="label" tick={{fill:C.t3,fontSize:10}}/><YAxis tick={{fill:C.t4,fontSize:8}}/><Tooltip content={<ChartTip/>}/><Legend wrapperStyle={{fontSize:9}}/>
                  {Object.entries(INTENTS).filter(([k])=>k!=='neutral').map(([k,d])=><Bar key={k} dataKey={k} stackId="a" fill={d.color} name={`${d.icon} ${k}`}/>)}
                </BarChart>
              </ResponsiveContainer>
            </Body>
          </Card>
        </>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// TRENDS TAB
// ─────────────────────────────────────────────────────────────────────────────
function TrendsTab({ texts, rows, col, config }) {
  const [gran,setGran]=useState('month');
  const { dateCol } = config;
  const hasDate=!!dateCol;

  const sentTrend=useMemo(()=>{ if(!hasDate)return[]; return bucketByTime(rows.slice(0,texts.length),dateCol,r=>sentiment(String(r[col]||'')).score,gran); },[rows,texts,col,dateCol,gran]);
  const volTrend =useMemo(()=>{ if(!hasDate)return[]; return bucketByTime(rows.slice(0,texts.length),dateCol,()=>1,gran); },[rows,texts,dateCol,gran]);

  const emotionTrend=useMemo(()=>{
    if(!hasDate)return[];
    const b={};
    rows.slice(0,texts.length).forEach((r,i)=>{
      const d=parseDate(r[dateCol]); if(!d) return;
      let key=gran==='day'?d.toISOString().slice(0,10):gran==='week'?(()=>{const w=new Date(d);w.setDate(d.getDate()-d.getDay());return w.toISOString().slice(0,10);})():`${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}`;
      if(!b[key]){b[key]={period:key};Object.keys(EMOTIONS).forEach(k=>{b[key][k]=0;b[key][k+'_n']=0;});}
      const e=detectEmotions(String(r[col]||''));
      Object.keys(EMOTIONS).forEach(k=>{b[key][k]+=(e.norm[k]||0);b[key][k+'_n']++;});
    });
    return Object.entries(b).sort((a,b)=>a[0].localeCompare(b[0])).map(([,v])=>{
      const res={period:v.period};
      Object.keys(EMOTIONS).forEach(k=>{res[k]=v[k+'_n']>0?+(v[k]/v[k+'_n']*100).toFixed(1):0;});
      return res;
    });
  },[rows,texts,col,dateCol,gran]);

  const intentTrend=useMemo(()=>{
    if(!hasDate)return[];
    const b={};
    rows.slice(0,texts.length).forEach((r,i)=>{
      const d=parseDate(r[dateCol]); if(!d) return;
      let key=gran==='day'?d.toISOString().slice(0,10):gran==='week'?(()=>{const w=new Date(d);w.setDate(d.getDate()-d.getDay());return w.toISOString().slice(0,10);})():`${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}`;
      if(!b[key]){b[key]={period:key};Object.keys(INTENTS).forEach(k=>{b[key][k]=0;});}
      const it=detectIntent(String(r[col]||''));
      b[key][it.primary]=(b[key][it.primary]||0)+1;
    });
    return Object.entries(b).sort((a,b)=>a[0].localeCompare(b[0])).map(([,v])=>({...v}));
  },[rows,texts,col,dateCol,gran]);

  if(!hasDate) return(
    <div style={{padding:60,textAlign:'center'}}>
      <div style={{fontSize:36,marginBottom:12,opacity:0.2}}>📅</div>
      <div style={{color:C.t4,fontSize:13}}>No date column selected.</div>
      <div style={{color:C.t4,fontSize:11,marginTop:6}}>Go to SETUP and pick a DATE column to unlock temporal trend analysis.</div>
    </div>
  );

  const peakPos=sentTrend.length>0?sentTrend.reduce((m,b)=>b.avg>m.avg?b:m,sentTrend[0]).period:'—';
  const peakNeg=sentTrend.length>0?sentTrend.reduce((m,b)=>b.avg<m.avg?b:m,sentTrend[0]).period:'—';
  const peakVol=volTrend.length>0?volTrend.reduce((m,b)=>b.count>m.count?b:m,volTrend[0]).period:'—';

  return(
    <div style={{display:'flex',flexDirection:'column',gap:14}}>
      <div style={{display:'flex',gap:6,alignItems:'center'}}>
        <span style={{fontSize:10,color:C.t3,fontFamily:'JetBrains Mono,monospace'}}>GRANULARITY:</span>
        {[['day','DAY'],['week','WEEK'],['month','MONTH']].map(([v,l])=>(
          <button key={v} onClick={()=>setGran(v)} style={{padding:'5px 12px',border:`1px solid ${gran===v?C.green:C.b0}`,background:gran===v?C.green+'15':'transparent',color:gran===v?C.green:C.t3,fontSize:9,borderRadius:3,cursor:'pointer',fontFamily:'JetBrains Mono,monospace',fontWeight:gran===v?700:400}}>{l}</button>
        ))}
        <span style={{fontSize:9,color:C.t4,marginLeft:8}}>{sentTrend.length} periods · {col} over {dateCol}</span>
      </div>

      <div style={{display:'grid',gridTemplateColumns:'repeat(4,1fr)',gap:10}}>
        <MB label="PERIODS"       value={sentTrend.length}  color={C.green}  sub={`by ${gran}`}/>
        <MB label="PEAK POSITIVE" value={peakPos}           color={C.green}  sub="highest avg sentiment"/>
        <MB label="PEAK NEGATIVE" value={peakNeg}           color={C.red}    sub="lowest avg sentiment"/>
        <MB label="PEAK VOLUME"   value={peakVol}           color={C.cyan}   sub="most documents"/>
      </div>

      <Card>
        <Head label="SENTIMENT OVER TIME" accent={C.green} right={<PinButton chart={{id:'deep_trend_sent',type:'area',title:'Sentiment Over Time'}} small/>}/>
        <Body>
          <ResponsiveContainer width="100%" height={240}>
            <AreaChart data={sentTrend}>
              <defs>
                <linearGradient id="tGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor={C.green} stopOpacity={0.3}/><stop offset="50%" stopColor="transparent" stopOpacity={0}/><stop offset="95%" stopColor={C.red} stopOpacity={0.2}/>
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="2 2" stroke={C.b0}/><XAxis dataKey="period" tick={{fill:C.t4,fontSize:8}} angle={-30} textAnchor="end" height={40}/><YAxis domain={[-1,1]} tick={{fill:C.t4,fontSize:8}}/>
              <Tooltip content={<ChartTip/>}/>
              <Area type="monotone" dataKey="avg" stroke={C.green} fill="url(#tGrad)" strokeWidth={2} name="avg sentiment"/>
            </AreaChart>
          </ResponsiveContainer>
        </Body>
      </Card>

      <Card>
        <Head label="DOCUMENT VOLUME OVER TIME" accent={C.cyan} right={<PinButton chart={{id:'deep_trend_vol',type:'bar',title:'Volume Over Time'}} small/>}/>
        <Body>
          <ResponsiveContainer width="100%" height={160}>
            <BarChart data={volTrend} barCategoryGap="8%">
              <CartesianGrid strokeDasharray="2 2" stroke={C.b0}/><XAxis dataKey="period" tick={{fill:C.t4,fontSize:8}} angle={-30} textAnchor="end" height={40}/><YAxis tick={{fill:C.t4,fontSize:8}}/><Tooltip content={<ChartTip/>}/>
              <Bar dataKey="count" fill={C.cyan} opacity={0.85} radius={[2,2,0,0]} name="documents"/>
            </BarChart>
          </ResponsiveContainer>
        </Body>
      </Card>

      {emotionTrend.length>0&&(
        <Card>
          <Head label="EMOTION BREAKDOWN OVER TIME" accent={C.gold} right={<PinButton chart={{id:'deep_trend_em',type:'bar',title:'Emotion Trends'}} small/>}/>
          <Body>
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={emotionTrend} barCategoryGap="5%">
                <CartesianGrid strokeDasharray="2 2" stroke={C.b0}/><XAxis dataKey="period" tick={{fill:C.t4,fontSize:8}} angle={-30} textAnchor="end" height={40}/><YAxis tick={{fill:C.t4,fontSize:8}}/><Tooltip content={<ChartTip/>}/><Legend wrapperStyle={{fontSize:9}}/>
                {Object.entries(EMOTIONS).map(([k,e])=><Bar key={k} dataKey={k} stackId="a" fill={e.color} name={`${e.icon} ${k}`}/>)}
              </BarChart>
            </ResponsiveContainer>
          </Body>
        </Card>
      )}

      {intentTrend.length>0&&(
        <Card>
          <Head label="INTENT BREAKDOWN OVER TIME" accent={C.cyan} right={<PinButton chart={{id:'deep_trend_int',type:'bar',title:'Intent Trends'}} small/>}/>
          <Body>
            <ResponsiveContainer width="100%" height={180}>
              <BarChart data={intentTrend} barCategoryGap="5%">
                <CartesianGrid strokeDasharray="2 2" stroke={C.b0}/><XAxis dataKey="period" tick={{fill:C.t4,fontSize:8}} angle={-30} textAnchor="end" height={40}/><YAxis tick={{fill:C.t4,fontSize:8}}/><Tooltip content={<ChartTip/>}/><Legend wrapperStyle={{fontSize:9}}/>
                {Object.entries(INTENTS).filter(([k])=>k!=='neutral').map(([k,d])=><Bar key={k} dataKey={k} stackId="a" fill={d.color} name={`${d.icon} ${k}`}/>)}
              </BarChart>
            </ResponsiveContainer>
          </Body>
        </Card>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// SIGNALS TAB
// ─────────────────────────────────────────────────────────────────────────────
function SignalsTab({ texts, rows, col, config }) {
  const [sortBy,setSortBy]=useState('overall');
  const [threshold,setThreshold]=useState(4);

  const scored=useMemo(()=>texts.map((t,i)=>({idx:i,text:t,row:rows[i]||{},...scoreSignals(t)})),[texts,rows]);

  const avgU  =+(scored.reduce((a,s)=>a+s.urgencyScore,0)/scored.length).toFixed(2);
  const avgC  =+(scored.reduce((a,s)=>a+s.churnRisk,0)/scored.length).toFixed(2);
  const avgE  =+(scored.reduce((a,s)=>a+s.escalationRisk,0)/scored.length).toFixed(2);
  const highRisk    =scored.filter(s=>s.overallRisk>=6).length;
  const criticalRisk=scored.filter(s=>s.overallRisk>=8).length;

  const riskDist=useMemo(()=>{
    const bins=Array.from({length:10},(_,i)=>({range:`${i+1}`,count:0}));
    scored.forEach(s=>{const idx=Math.min(9,Math.floor(s.overallRisk));if(idx>=0)bins[idx].count++;});
    return bins;
  },[scored]);

  const topRisk=useMemo(()=>{
    const key=sortBy==='overall'?'overallRisk':sortBy==='churn'?'churnRisk':sortBy==='urgency'?'urgencyScore':'escalationRisk';
    return [...scored].sort((a,b)=>b[key]-a[key]).filter(s=>s.overallRisk>=threshold);
  },[scored,sortBy,threshold]);

  return(
    <div style={{display:'flex',flexDirection:'column',gap:14}}>
      <div style={{display:'grid',gridTemplateColumns:'repeat(5,1fr)',gap:10}}>
        <MB label="AVG URGENCY"    value={avgU}         color={C.orange} sub="/10"/>
        <MB label="AVG CHURN RISK" value={avgC}         color={C.red}    sub="/10"/>
        <MB label="AVG ESCALATION" value={avgE}         color={C.violet} sub="/10"/>
        <MB label="HIGH RISK ≥6"   value={highRisk}     color={C.red}    sub={`${(highRisk/scored.length*100).toFixed(1)}%`}/>
        <MB label="CRITICAL ≥8"    value={criticalRisk} color={C.red}    sub="needs action"/>
      </div>

      {criticalRisk>0&&<Info color={C.red}>🚨 {criticalRisk} CRITICAL documents (risk ≥ 8) need immediate attention. Likely urgent complaints, churn signals, or escalation threats. Review in table below.</Info>}

      <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:14}}>
        <Card>
          <Head label="RISK SCORE DISTRIBUTION" accent={C.red} right={<PinButton chart={{id:'deep_risk_dist',type:'bar',title:'Risk Score Distribution'}} small/>}/>
          <Body>
            <ResponsiveContainer width="100%" height={180}>
              <BarChart data={riskDist} barCategoryGap="5%">
                <CartesianGrid strokeDasharray="2 2" stroke={C.b0}/><XAxis dataKey="range" tick={{fill:C.t4,fontSize:9}}/><YAxis tick={{fill:C.t4,fontSize:8}}/><Tooltip content={<ChartTip/>}/>
                <Bar dataKey="count" name="documents" radius={[2,2,0,0]}>{riskDist.map((d,i)=><Cell key={i} fill={i>=7?C.red:i>=4?C.orange:C.t3}/>)}</Bar>
              </BarChart>
            </ResponsiveContainer>
          </Body>
        </Card>
        <Card>
          <Head label="SIGNAL BREAKDOWN" accent={C.orange}/>
          <Body>
            <RiskBar label="AVG URGENCY SCORE"   value={avgU} color={C.orange}/>
            <RiskBar label="AVG CHURN RISK"      value={avgC} color={C.red}/>
            <RiskBar label="AVG ESCALATION RISK" value={avgE} color={C.violet}/>
            <div style={{marginTop:14}}>
              <div style={{fontSize:9,color:C.t3,fontFamily:'JetBrains Mono,monospace',marginBottom:8}}>SIGNAL DRIVERS</div>
              <div style={{fontSize:10,color:C.t2,lineHeight:1.9}}>
                <strong style={{color:C.orange}}>Urgency</strong> — urgent, asap, critical, broken, cannot, outage + exclamation marks<br/>
                <strong style={{color:C.red}}>Churn Risk</strong> — cancel, leaving, switch, competitor, refund, never again<br/>
                <strong style={{color:C.violet}}>Escalation</strong> — manager, legal, lawyer, sue, BBB, media, expose<br/>
                <strong style={{color:C.gold}}>Spam</strong> — repeated chars, all-caps runs, click/buy/cheap/free patterns
              </div>
            </div>
          </Body>
        </Card>
      </div>

      <Card>
        <Head label={`HIGH-RISK DOCUMENTS — ${topRisk.length} docs with overall risk ≥ ${threshold}`} accent={C.red}/>
        <Body style={{paddingBottom:0}}>
          <div style={{display:'flex',gap:8,marginBottom:10,alignItems:'center',flexWrap:'wrap'}}>
            <span style={{fontSize:9,color:C.t3,fontFamily:'JetBrains Mono,monospace'}}>SORT BY:</span>
            {[['overall','OVERALL'],['urgency','URGENCY'],['churn','CHURN'],['escalation','ESCALATION']].map(([v,l])=>(
              <button key={v} onClick={()=>setSortBy(v)} style={{padding:'4px 9px',border:`1px solid ${sortBy===v?C.red:C.b1}`,background:sortBy===v?C.red+'18':'transparent',color:sortBy===v?C.red:C.t3,fontSize:8,borderRadius:2,cursor:'pointer',fontFamily:'JetBrains Mono,monospace',fontWeight:700}}>{l}</button>
            ))}
            <span style={{fontSize:9,color:C.t3,marginLeft:8}}>MIN RISK:</span>
            <input type="range" min={1} max={9} value={threshold} onChange={e=>setThreshold(+e.target.value)} style={{width:80,accentColor:C.red}}/>
            <span style={{fontSize:9,color:C.red,fontWeight:700}}>{threshold}/10</span>
          </div>
        </Body>
        <div style={{maxHeight:480,overflowY:'auto'}}>
          <table style={{width:'100%',borderCollapse:'collapse',fontSize:11}}>
            <thead style={{position:'sticky',top:0,zIndex:1}}><tr style={{background:C.deep}}>{['#','TEXT PREVIEW','OVERALL','URGENCY','CHURN','ESCALATION','SPAM'].map(h=><th key={h} style={TH}>{h}</th>)}</tr></thead>
            <tbody>
              {topRisk.slice(0,80).map((s,i)=>{
                const rC=s.overallRisk>=8?C.red:s.overallRisk>=5?C.orange:C.t3;
                return(
                  <tr key={s.idx} style={{borderBottom:`1px solid ${C.b0}`,background:s.overallRisk>=8?C.red+'08':'transparent'}}>
                    <td style={{...TD,color:C.t4}}>{s.idx+1}</td>
                    <td style={{...TD,color:C.t2,maxWidth:300}}><div style={{overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{s.text.slice(0,120)}</div></td>
                    <td style={{...TD}}><span style={{color:rC,fontWeight:800,fontFamily:'Syne,sans-serif',fontSize:14}}>{s.overallRisk.toFixed(1)}</span></td>
                    <td style={{...TD,color:s.urgencyScore>5?C.orange:C.t3}}>{s.urgencyScore}/10</td>
                    <td style={{...TD,color:s.churnRisk>5?C.red:C.t3}}>{s.churnRisk.toFixed(1)}/10</td>
                    <td style={{...TD,color:s.escalationRisk>5?C.violet:C.t3}}>{s.escalationRisk}/10</td>
                    <td style={{...TD,color:s.spamScore>3?C.gold:C.t4}}>{s.spamScore}/10</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// VOICE TAB — pain points, feature requests, praise themes
// ─────────────────────────────────────────────────────────────────────────────
function VoiceTab({ texts }) {
  const voice=useMemo(()=>texts.length?extractVoice(texts):{pains:[],reqs:[],praises:[]},[texts]);

  const VoiceSection=({title,items,color,icon,desc})=>(
    <Card>
      <Head label={`${icon} ${title} — ${items.length} THEMES`} accent={color}/>
      <Body>
        <div style={{fontSize:10,color:C.t3,marginBottom:10}}>{desc}</div>
        {items.length===0&&<div style={{color:C.t4,fontSize:11,padding:'20px 0',textAlign:'center'}}>No patterns detected. Try with customer reviews, support tickets, or survey text.</div>}
        {items.map((item,i)=>(
          <div key={i} style={{display:'flex',alignItems:'center',gap:10,padding:'6px 0',borderBottom:`1px solid ${C.b0}`}}>
            <span style={{fontFamily:'JetBrains Mono,monospace',fontSize:9,color,fontWeight:700,minWidth:22}}>{i+1}</span>
            <div style={{flex:1,fontSize:11,color:C.t1}}>{item.text}</div>
            <div style={{display:'flex',alignItems:'center',gap:6,flexShrink:0}}>
              <span style={{fontFamily:'Syne,sans-serif',fontWeight:800,fontSize:14,color}}>{item.count}</span>
              <div style={{width:60}}><MBar pct={item.count/items[0].count*100} color={color}/></div>
            </div>
          </div>
        ))}
      </Body>
    </Card>
  );

  return(
    <div style={{display:'flex',flexDirection:'column',gap:14}}>
      <div style={{display:'grid',gridTemplateColumns:'repeat(3,1fr)',gap:10}}>
        <MB label="PAIN POINTS DETECTED"   value={voice.pains.length}   color={C.red}    sub="extracted themes"/>
        <MB label="FEATURE REQUESTS"       value={voice.reqs.length}    color={C.cyan}   sub="extracted themes"/>
        <MB label="PRAISE THEMES"          value={voice.praises.length} color={C.green}  sub="extracted themes"/>
      </div>
      <Info color={C.violet}>Voice of Customer patterns work best with customer reviews, support tickets, NPS responses, and survey text — language that naturally expresses problems, requests, and praise.</Info>
      <VoiceSection title="PAIN POINTS"     items={voice.pains}   color={C.red}   icon="⚠"  desc="What customers struggle with — extracted from complaint and problem-language patterns."/>
      <VoiceSection title="FEATURE REQUESTS" items={voice.reqs}  color={C.cyan}  icon="💡" desc="What customers want built — extracted from request and suggestion language."/>
      <VoiceSection title="PRAISE THEMES"   items={voice.praises} color={C.green} icon="⭐" desc="What customers love — extracted from praise and recommendation language."/>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// A/B COMPARE TAB
// ─────────────────────────────────────────────────────────────────────────────
function CompareTab({ texts, rows, col, config }) {
  const [groupA,setGroupA]=useState('');
  const [groupB,setGroupB]=useState('');
  const [result,setResult]=useState(null);

  const groups=useMemo(()=>{
    if(!config.groupCol)return[];
    const g={};
    rows.slice(0,texts.length).forEach((r,i)=>{ const v=r[config.groupCol]||'—'; if(!g[v])g[v]=[]; g[v].push(texts[i]); });
    return Object.entries(g).sort((a,b)=>b[1].length-a[1].length).slice(0,20);
  },[rows,texts,config]);

  const analyze=useCallback(txts=>{
    const sents=txts.map(t=>sentiment(t));
    const ems=txts.map(t=>detectEmotions(t));
    const ints=txts.map(t=>detectIntent(t));
    const sigs=txts.map(t=>scoreSignals(t));
    const freq={};
    txts.forEach(t=>tok(t,true).forEach(w=>{freq[w]=(freq[w]||0)+1;}));
    const topWords=Object.entries(freq).sort((a,b)=>b[1]-a[1]).slice(0,15).map(([w,c])=>({word:w,count:c}));
    const intentCounts={};Object.keys(INTENTS).forEach(k=>{intentCounts[k]=ints.filter(i=>i.primary===k).length;});
    const emCounts={};Object.keys(EMOTIONS).forEach(k=>{emCounts[k]=ems.filter(e=>e.dominant===k).length;});
    return {
      count:txts.length,
      avgSentiment:+(sents.reduce((a,s)=>a+s.score,0)/Math.max(sents.length,1)).toFixed(4),
      posCount:sents.filter(s=>s.label==='positive').length,
      negCount:sents.filter(s=>s.label==='negative').length,
      avgUrgency:+(sigs.reduce((a,s)=>a+s.urgencyScore,0)/Math.max(sigs.length,1)).toFixed(2),
      avgChurn:+(sigs.reduce((a,s)=>a+s.churnRisk,0)/Math.max(sigs.length,1)).toFixed(2),
      topWords,intentCounts,emCounts,
    };
  },[]);

  const run=()=>{
    if(!groupA||!groupB)return;
    const txtsA=groups.find(([k])=>k===groupA)?.[1]||[];
    const txtsB=groups.find(([k])=>k===groupB)?.[1]||[];
    setResult({a:{label:groupA,...analyze(txtsA)},b:{label:groupB,...analyze(txtsB)}});
  };

  if(!config.groupCol) return(
    <div style={{padding:60,textAlign:'center'}}>
      <div style={{fontSize:36,marginBottom:12,opacity:0.2}}>⚖</div>
      <div style={{color:C.t4,fontSize:13}}>No GROUP column selected.</div>
      <div style={{color:C.t4,fontSize:11,marginTop:6}}>Go to SETUP and pick a GROUP / CATEGORY column (e.g. product, source, rating, region).</div>
    </div>
  );

  const CompareMetric=({label,a,b,better='higher'})=>{
    const aWins=better==='higher'?a>b:a<b;
    return(
      <div style={{padding:'10px 14px',background:C.raised,borderRadius:3,marginBottom:8}}>
        <div style={{fontSize:8,color:C.t3,fontFamily:'JetBrains Mono,monospace',marginBottom:6}}>{label}</div>
        <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:10}}>
          {[{val:a,label:result.a.label,wins:aWins,color:C.green},{val:b,label:result.b.label,wins:!aWins,color:C.cyan}].map((side,si)=>(
            <div key={si} style={{textAlign:'center',background:side.wins?side.color+'12':'transparent',borderRadius:3,padding:6,border:side.wins?`1px solid ${side.color}33`:'none'}}>
              <div style={{fontFamily:'Syne,sans-serif',fontWeight:800,fontSize:20,color:side.wins?side.color:C.t2}}>{typeof side.val==='number'?side.val.toFixed(2):side.val}</div>
              <div style={{fontSize:8,color:C.t4}}>{side.label}</div>
              {side.wins&&<div style={{fontSize:8,color:side.color,marginTop:2}}>▲ BETTER</div>}
            </div>
          ))}
        </div>
      </div>
    );
  };

  return(
    <div style={{display:'flex',flexDirection:'column',gap:14}}>
      <Card>
        <Head label="GROUP SELECTOR" accent={C.gold} sub={`${groups.length} unique values in ${config.groupCol}`}/>
        <Body>
          <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:14}}>
            {[['GROUP A',groupA,setGroupA,C.green],['GROUP B',groupB,setGroupB,C.cyan]].map(([label,val,setter,color])=>(
              <div key={label}>
                <Label>{label}</Label>
                <select value={val} onChange={e=>setter(e.target.value)} style={{width:'100%',background:C.deep,border:`2px solid ${color}`,color:C.t1,padding:'7px 10px',fontSize:11,fontFamily:'JetBrains Mono,monospace',borderRadius:3,outline:'none'}}>
                  <option value="">— select —</option>
                  {groups.map(([g,txts])=><option key={g} value={g}>{g} ({txts.length} docs)</option>)}
                </select>
              </div>
            ))}
          </div>
          <div style={{marginTop:12}}><Btn onClick={run} color={C.gold} disabled={!groupA||!groupB}>⚖ COMPARE →</Btn></div>
        </Body>
      </Card>

      {result&&(
        <div style={{display:'flex',flexDirection:'column',gap:14}}>
          <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:14}}>
            <div>
              <CompareMetric label="AVG SENTIMENT"   a={result.a.avgSentiment} b={result.b.avgSentiment} better="higher"/>
              <CompareMetric label="POSITIVE DOCS"   a={result.a.posCount}     b={result.b.posCount}     better="higher"/>
              <CompareMetric label="NEGATIVE DOCS"   a={result.a.negCount}     b={result.b.negCount}     better="lower"/>
              <CompareMetric label="AVG URGENCY"     a={result.a.avgUrgency}   b={result.b.avgUrgency}   better="lower"/>
              <CompareMetric label="AVG CHURN RISK"  a={result.a.avgChurn}     b={result.b.avgChurn}     better="lower"/>
            </div>
            <div style={{display:'flex',flexDirection:'column',gap:12}}>
              <Card>
                <Head label="INTENT COMPARISON" accent={C.gold} right={<PinButton chart={{id:'deep_ab_intent',type:'bar',title:'A/B Intent'}} small/>}/>
                <Body>
                  <ResponsiveContainer width="100%" height={200}>
                    <BarChart data={Object.keys(INTENTS).filter(k=>k!=='neutral').map(k=>({intent:k,[result.a.label]:result.a.intentCounts[k]||0,[result.b.label]:result.b.intentCounts[k]||0}))} barCategoryGap="15%">
                      <CartesianGrid strokeDasharray="2 2" stroke={C.b0}/><XAxis dataKey="intent" tick={{fill:C.t3,fontSize:9}}/><YAxis tick={{fill:C.t4,fontSize:8}}/><Tooltip content={<ChartTip/>}/>
                      <Bar dataKey={result.a.label} fill={C.green} radius={[2,2,0,0]} opacity={0.85}/>
                      <Bar dataKey={result.b.label} fill={C.cyan}  radius={[2,2,0,0]} opacity={0.85}/>
                    </BarChart>
                  </ResponsiveContainer>
                </Body>
              </Card>
              <Card>
                <Head label="EMOTION COMPARISON" accent={C.gold} right={<PinButton chart={{id:'deep_ab_em',type:'bar',title:'A/B Emotion'}} small/>}/>
                <Body>
                  <ResponsiveContainer width="100%" height={200}>
                    <BarChart data={Object.keys(EMOTIONS).map(k=>({em:`${EMOTIONS[k].icon}`,[result.a.label]:result.a.emCounts[k]||0,[result.b.label]:result.b.emCounts[k]||0}))} barCategoryGap="10%">
                      <CartesianGrid strokeDasharray="2 2" stroke={C.b0}/><XAxis dataKey="em" tick={{fill:C.t3,fontSize:12}}/><YAxis tick={{fill:C.t4,fontSize:8}}/><Tooltip content={<ChartTip/>}/>
                      <Bar dataKey={result.a.label} fill={C.green} radius={[2,2,0,0]} opacity={0.85}/>
                      <Bar dataKey={result.b.label} fill={C.cyan}  radius={[2,2,0,0]} opacity={0.85}/>
                    </BarChart>
                  </ResponsiveContainer>
                </Body>
              </Card>
            </div>
          </div>
          <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:14}}>
            {[result.a,result.b].map((g,gi)=>(
              <Card key={g.label}>
                <Head label={`TOP TERMS — ${g.label}`} accent={gi===0?C.green:C.cyan}/>
                <Body>
                  {g.topWords.slice(0,12).map((w,i)=>(
                    <div key={w.word} style={{display:'flex',alignItems:'center',gap:8,padding:'4px 0',borderBottom:`1px solid ${C.b0}`}}>
                      <span style={{fontFamily:'JetBrains Mono,monospace',fontSize:10,color:gi===0?C.green:C.cyan,fontWeight:700,minWidth:110}}>{w.word}</span>
                      <span style={{fontSize:10,color:C.t2,minWidth:30}}>{w.count}</span>
                      <div style={{flex:1}}><MBar pct={w.count/g.topWords[0].count*100} color={gi===0?C.green:C.cyan}/></div>
                    </div>
                  ))}
                </Body>
              </Card>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// ANNOTATE TAB — manual labeling + enriched CSV export for ML training
// ─────────────────────────────────────────────────────────────────────────────
const TAG_COLORS=['#22d3ee','#4ade80','#f87171','#fbbf24','#a78bfa','#fb923c','#f472b6','#60a5fa','#34d399','#67e8f9'];
const DEFAULT_TAGS=['positive','negative','neutral','complaint','request','spam'];

function AnnotateTab({ texts, rows, col, ds }) {
  const [tags,setTags]=useState(DEFAULT_TAGS);
  const [newTag,setNewTag]=useState('');
  const [labels,setLabels]=useState({});
  const [filter,setFilter]=useState('all');
  const [search,setSearch]=useState('');
  const [page,setPage]=useState(0);
  const PAGE=20;

  const addTag=()=>{ if(newTag.trim()&&!tags.includes(newTag.trim())){setTags(t=>[...t,newTag.trim()]);setNewTag('');} };
  const setLabel=(idx,tag)=>setLabels(l=>({...l,[idx]:l[idx]===tag?undefined:tag}));

  const labeled=Object.keys(labels).length;

  const filtered=useMemo(()=>{
    let s=filter==='all'?texts.map((t,i)=>({i,t})):filter==='unlabeled'?texts.map((t,i)=>({i,t})).filter(({i})=>!labels[i]):filter==='labeled'?texts.map((t,i)=>({i,t})).filter(({i})=>!!labels[i]):texts.map((t,i)=>({i,t})).filter(({i})=>labels[i]===filter);
    if(search.trim())s=s.filter(({t})=>t.toLowerCase().includes(search.toLowerCase()));
    return s;
  },[texts,labels,filter,search]);

  const paged=filtered.slice(page*PAGE,(page+1)*PAGE);

  const exportCSV=()=>{
    const out=texts.map((t,i)=>{
      const r=rows[i]||{};
      const s=sentiment(t),e=detectEmotions(t),it=detectIntent(t),sig=scoreSignals(t);
      return {...r,[col]:t,_manual_label:labels[i]||'',_sentiment:s.label,_sentiment_score:s.score,_intent:it.primary,_emotion:e.dominant,_urgency:sig.urgencyScore,_churn_risk:sig.churnRisk,_escalation_risk:sig.escalationRisk,_overall_risk:sig.overallRisk};
    });
    const hdrs=Object.keys(out[0]);
    const csv=[hdrs.join(','),...out.map(r=>hdrs.map(h=>`"${String(r[h]||'').replace(/"/g,'""')}"`).join(','))].join('\n');
    const a=Object.assign(document.createElement('a'),{href:URL.createObjectURL(new Blob([csv],{type:'text/csv'})),download:'nlp-deep-labeled.csv'});
    a.click();
  };

  return(
    <div style={{display:'flex',flexDirection:'column',gap:14}}>
      <div style={{display:'grid',gridTemplateColumns:'1fr 1fr 1fr 1fr',gap:10}}>
        <MB label="TOTAL DOCS"  value={texts.length} color={C.violet}/>
        <MB label="LABELED"     value={labeled}      color={C.green}  sub={`${(labeled/Math.max(texts.length,1)*100).toFixed(1)}%`}/>
        <MB label="UNLABELED"   value={texts.length-labeled} color={C.t3} sub="remaining"/>
        <div style={{display:'flex',alignItems:'center',justifyContent:'center'}}>
          <button onClick={exportCSV} style={{padding:'12px 18px',background:C.green+'18',border:`2px solid ${C.green}`,color:C.green,fontSize:10,cursor:'pointer',borderRadius:4,fontFamily:'JetBrains Mono,monospace',fontWeight:700,letterSpacing:'.1em'}}>↓ EXPORT LABELED CSV</button>
        </div>
      </div>

      <Info color={C.violet}>Export auto-enriches every row with: sentiment, intent, dominant emotion, urgency score, churn risk, escalation risk + your manual labels. Drop this CSV directly into ML training pipelines.</Info>

      <Card>
        <Head label="LABEL TAXONOMY — CUSTOMIZE YOUR LABELS" accent={C.violet}/>
        <Body>
          <div style={{display:'flex',flexWrap:'wrap',gap:8,marginBottom:12}}>
            {tags.map((t,i)=>(
              <div key={t} style={{display:'flex',alignItems:'center',gap:5,padding:'4px 10px',background:TAG_COLORS[i%TAG_COLORS.length]+'15',border:`1px solid ${TAG_COLORS[i%TAG_COLORS.length]}55`,borderRadius:3}}>
                <span style={{fontSize:11,color:TAG_COLORS[i%TAG_COLORS.length],fontWeight:700}}>{t}</span>
                <button onClick={()=>setTags(tt=>tt.filter(l=>l!==t))} style={{background:'none',border:'none',color:TAG_COLORS[i%TAG_COLORS.length],cursor:'pointer',fontSize:14,lineHeight:1,padding:0}}>×</button>
              </div>
            ))}
          </div>
          <div style={{display:'flex',gap:8}}>
            <input value={newTag} onChange={e=>setNewTag(e.target.value)} onKeyDown={e=>e.key==='Enter'&&addTag()} placeholder="Add custom label (e.g. bug, feature, billing)…" style={{flex:1,background:C.deep,border:`1px solid ${C.b2}`,color:C.t1,padding:'6px 10px',fontSize:11,fontFamily:'JetBrains Mono,monospace',borderRadius:3,outline:'none'}}/>
            <Btn onClick={addTag} color={C.violet}>+ ADD</Btn>
          </div>
        </Body>
      </Card>

      <div style={{display:'flex',gap:6,alignItems:'center',flexWrap:'wrap'}}>
        {['all','unlabeled','labeled',...tags].map(f=>(
          <button key={f} onClick={()=>{setFilter(f);setPage(0);}} style={{padding:'4px 9px',border:`1px solid ${filter===f?C.violet:C.b1}`,background:filter===f?C.violet+'18':'transparent',color:filter===f?C.violet:C.t3,fontSize:8,borderRadius:2,cursor:'pointer',fontFamily:'JetBrains Mono,monospace',fontWeight:700}}>{f.toUpperCase()}</button>
        ))}
        <div style={{marginLeft:'auto',display:'flex',gap:6,alignItems:'center'}}>
          <span style={{fontSize:9,color:C.t4}}>{page*PAGE+1}–{Math.min((page+1)*PAGE,filtered.length)} of {filtered.length}</span>
          <button onClick={()=>setPage(p=>Math.max(0,p-1))} disabled={page===0} style={{padding:'3px 8px',background:'none',border:`1px solid ${C.b1}`,color:C.t3,cursor:'pointer',borderRadius:2,fontSize:10}}>←</button>
          <button onClick={()=>setPage(p=>Math.min(Math.ceil(filtered.length/PAGE)-1,p+1))} disabled={(page+1)*PAGE>=filtered.length} style={{padding:'3px 8px',background:'none',border:`1px solid ${C.b1}`,color:C.t3,cursor:'pointer',borderRadius:2,fontSize:10}}>→</button>
        </div>
      </div>

      <div style={{display:'flex',flexDirection:'column',gap:8}}>
        {paged.map(({i,t})=>{
          const cur=labels[i];
          const sig=scoreSignals(t),e=detectEmotions(t),s=sentiment(t),it=detectIntent(t);
          const curColor=cur?TAG_COLORS[tags.indexOf(cur)%TAG_COLORS.length]:C.b1;
          return(
            <Card key={i} style={{borderLeft:`3px solid ${curColor}`}}>
              <div style={{padding:'10px 14px'}}>
                <div style={{display:'flex',justifyContent:'space-between',alignItems:'flex-start',gap:12,marginBottom:8}}>
                  <div style={{flex:1}}>
                    <div style={{fontSize:8,color:C.t4,fontFamily:'JetBrains Mono,monospace',marginBottom:4}}>DOC #{i+1}</div>
                    <div style={{fontSize:11,color:C.t1,lineHeight:1.65}}>{t.slice(0,300)}{t.length>300&&'…'}</div>
                  </div>
                  <div style={{display:'flex',flexDirection:'column',gap:4,flexShrink:0,minWidth:140}}>
                    <div style={{fontSize:8,color:C.t4,fontFamily:'JetBrains Mono,monospace'}}>AUTO-SIGNALS</div>
                    <Badge label={s.label.toUpperCase()} color={sentC(s.label)}/>
                    <Badge label={`${INTENTS[it.primary]?.icon||''} ${it.primary}`} color={intC(it.primary)}/>
                    <Badge label={`${EMOTIONS[e.dominant]?.icon||''} ${e.dominant}`} color={EMOTIONS[e.dominant]?.color||C.t3}/>
                    {sig.urgencyScore>=4&&<Badge label={`⚡ URG ${sig.urgencyScore}/10`} color={C.orange}/>}
                    {sig.churnRisk>=4&&<Badge label={`🚨 CHURN ${sig.churnRisk.toFixed(0)}/10`} color={C.red}/>}
                    {sig.escalationRisk>=4&&<Badge label={`⚠ ESC ${sig.escalationRisk}/10`} color={C.violet}/>}
                  </div>
                </div>
                <div style={{display:'flex',gap:6,flexWrap:'wrap',marginTop:6,paddingTop:8,borderTop:`1px solid ${C.b0}`}}>
                  {tags.map((tag,ti)=>{
                    const tc=TAG_COLORS[ti%TAG_COLORS.length];
                    const sel=cur===tag;
                    return <button key={tag} onClick={()=>setLabel(i,tag)} style={{padding:'4px 11px',border:`1px solid ${sel?tc:C.b1}`,background:sel?tc+'28':'transparent',color:sel?tc:C.t3,fontSize:9,borderRadius:3,cursor:'pointer',fontFamily:'JetBrains Mono,monospace',fontWeight:sel?700:400}}>{sel&&'✓ '}{tag}</button>;
                  })}
                  {cur&&<button onClick={()=>setLabel(i,cur)} style={{padding:'4px 10px',border:`1px solid ${C.red}33`,background:'transparent',color:C.red,fontSize:9,borderRadius:3,cursor:'pointer',fontFamily:'JetBrains Mono,monospace'}}>✕ clear</button>}
                </div>
              </div>
            </Card>
          );
        })}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// DEEP REPORT TAB
// ─────────────────────────────────────────────────────────────────────────────
function DeepReportTab({ experiment, texts, col, config, dashboardCards }) {
  const [notes,setNotes]=useState([]);
  const [noteText,setNoteText]=useState('');
  const [noteTag,setNoteTag]=useState('finding');
  const reportRef=useRef();

  const stats=useMemo(()=>{
    if(!texts.length)return null;
    const sents=texts.map(t=>sentiment(t));
    const avgSent=+(sents.reduce((a,s)=>a+s.score,0)/sents.length).toFixed(4);
    const posCount=sents.filter(s=>s.label==='positive').length;
    const negCount=sents.filter(s=>s.label==='negative').length;
    const ems=texts.map(t=>detectEmotions(t));
    const emDom={}; Object.keys(EMOTIONS).forEach(k=>{emDom[k]=0;}); ems.forEach(e=>{emDom[e.dominant]=(emDom[e.dominant]||0)+1;});
    const topEmotion=Object.entries(emDom).sort((a,b)=>b[1]-a[1])[0][0];
    const ints=texts.map(t=>detectIntent(t));
    const intDom={}; Object.keys(INTENTS).forEach(k=>{intDom[k]=0;}); ints.forEach(i=>{intDom[i.primary]=(intDom[i.primary]||0)+1;});
    const topIntent=Object.entries(intDom).sort((a,b)=>b[1]-a[1])[0][0];
    const sigs=texts.map(t=>scoreSignals(t));
    const highRisk=sigs.filter(s=>s.overallRisk>=6).length;
    const voice=extractVoice(texts);
    const freq={};texts.forEach(t=>tok(t,true).forEach(w=>{freq[w]=(freq[w]||0)+1;}));
    const topWords=Object.entries(freq).sort((a,b)=>b[1]-a[1]).slice(0,15).map(([w,c])=>({word:w,count:c}));
    return {avgSent,posCount,negCount,topEmotion,topIntent,highRisk,voice,topWords,total:texts.length,intDom,emDom,avgUrgency:+(sigs.reduce((a,s)=>a+s.urgencyScore,0)/sigs.length).toFixed(2),avgChurn:+(sigs.reduce((a,s)=>a+s.churnRisk,0)/sigs.length).toFixed(2)};
  },[texts]);

  const exportMD=()=>{
    if(!stats)return;
    const sentLabel=stats.avgSent>0.1?'Positive':stats.avgSent<-0.1?'Negative':'Neutral';
    const md=[
      `# NLP DEEP ANALYSIS REPORT\n**${experiment.name}** · ${new Date().toDateString()} · Column: \`${col}\`\n`,
      `## Executive Summary\n- ${stats.total} documents analysed\n- Overall sentiment: **${sentLabel}** (avg ${stats.avgSent})\n- Dominant emotion: **${stats.topEmotion}** ${EMOTIONS[stats.topEmotion]?.icon||''}\n- Primary intent: **${stats.topIntent}** ${INTENTS[stats.topIntent]?.icon||''}\n- High-risk documents: **${stats.highRisk}** (≥ 6/10)\n`,
      `## Sentiment\n- Positive: ${stats.posCount} (${(stats.posCount/stats.total*100).toFixed(1)}%)\n- Negative: ${stats.negCount} (${(stats.negCount/stats.total*100).toFixed(1)}%)\n- Neutral: ${stats.total-stats.posCount-stats.negCount}\n`,
      `## Risk Signals\n- Avg Urgency Score: ${stats.avgUrgency}/10\n- Avg Churn Risk: ${stats.avgChurn}/10\n- High-Risk Docs (≥6): ${stats.highRisk}\n`,
      `## Voice of Customer\nTop Pain Points:\n${stats.voice.pains.slice(0,5).map((p,i)=>`${i+1}. "${p.text}" (×${p.count})`).join('\n')}\n\nTop Requests:\n${stats.voice.reqs.slice(0,5).map((r,i)=>`${i+1}. "${r.text}" (×${r.count})`).join('\n')}\n`,
      `## Top Terms\n${stats.topWords.map((w,i)=>`${i+1}. **${w.word}** — ${w.count}`).join('\n')}\n`,
      notes.length>0?`## Research Notes\n${notes.map(n=>`**[${n.tag.toUpperCase()}]** ${n.text}`).join('\n\n')}\n`:'',
    ].join('\n');
    const a=Object.assign(document.createElement('a'),{href:URL.createObjectURL(new Blob([md],{type:'text/markdown'})),download:`nlp-deep-${experiment.name}.md`});
    a.click();
  };

  if(!stats)return<div style={{padding:40,textAlign:'center',color:C.t4}}>No text data. Select a column in SETUP first.</div>;

  const sentLabel=stats.avgSent>0.1?'Positive 😊':stats.avgSent<-0.1?'Negative 😞':'Neutral 😐';
  const sentColor=stats.avgSent>0.1?C.green:stats.avgSent<-0.1?C.red:C.t3;

  return(
    <div style={{display:'flex',flexDirection:'column',gap:14}}>
      <div style={{display:'flex',gap:6,alignItems:'center',flexWrap:'wrap',padding:'10px 14px',background:C.surface,border:`1px solid ${C.b1}`,borderRadius:4}}>
        <button onClick={exportMD} style={{padding:'6px 14px',background:C.cyan+'15',border:`1px solid ${C.cyan}`,color:C.cyan,fontFamily:'JetBrains Mono,monospace',fontSize:9,fontWeight:700,borderRadius:3,cursor:'pointer'}}>↓ EXPORT MARKDOWN</button>
      </div>

      <div ref={reportRef} style={{background:C.surface,border:`1px solid ${C.b1}`,borderRadius:6,padding:'32px 36px'}}>
        <h1 style={{fontFamily:'Syne,sans-serif',fontWeight:800,fontSize:26,color:C.t1,margin:'0 0 4px'}}>{experiment.name}</h1>
        <div style={{fontSize:9,color:C.t3,fontFamily:'JetBrains Mono,monospace',marginBottom:6}}>NLP DEEP ANALYSIS · {new Date().toLocaleDateString('en',{year:'numeric',month:'long',day:'numeric'})} · COLUMN: {col} · {stats.total} DOCUMENTS</div>
        <div style={{height:1,background:`linear-gradient(90deg,${C.violet}55 0%,transparent 80%)`,marginBottom:28}}/>

        <div style={{fontFamily:'Syne,sans-serif',fontWeight:800,fontSize:14,color:C.violet,marginBottom:12}}>EXECUTIVE SUMMARY</div>
        <div style={{display:'grid',gridTemplateColumns:'repeat(5,1fr)',gap:8,marginBottom:20}}>
          <MB label="DOCUMENTS"    value={stats.total}                            color={C.violet}/>
          <MB label="SENTIMENT"    value={sentLabel}                              color={sentColor}      sub={`avg ${stats.avgSent}`}/>
          <MB label="TOP EMOTION"  value={`${EMOTIONS[stats.topEmotion]?.icon||''} ${stats.topEmotion}`} color={EMOTIONS[stats.topEmotion]?.color||C.t3}/>
          <MB label="TOP INTENT"   value={`${INTENTS[stats.topIntent]?.icon||''} ${stats.topIntent}`}   color={INTENTS[stats.topIntent]?.color||C.t3}/>
          <MB label="HIGH RISK"    value={stats.highRisk}                         color={stats.highRisk>0?C.red:C.green} sub="docs ≥ 6/10"/>
        </div>

        <div style={{fontFamily:'Syne,sans-serif',fontWeight:800,fontSize:14,color:C.green,marginBottom:12}}>SENTIMENT BREAKDOWN</div>
        <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:14,marginBottom:20}}>
          <div>
            <div style={{height:12,borderRadius:4,background:C.b1,overflow:'hidden',display:'flex',marginBottom:8}}>
              {[[stats.posCount,C.green],[stats.total-stats.posCount-stats.negCount,C.t3],[stats.negCount,C.red]].map(([v,color],i)=>(v>0?<div key={i} style={{flex:v,background:color,opacity:0.75}}/>:null))}
            </div>
            <div style={{display:'grid',gridTemplateColumns:'1fr 1fr 1fr',gap:6}}>
              <MB label="POSITIVE" value={stats.posCount} color={C.green}  sub={`${(stats.posCount/stats.total*100).toFixed(0)}%`}/>
              <MB label="NEUTRAL"  value={stats.total-stats.posCount-stats.negCount} color={C.t3}/>
              <MB label="NEGATIVE" value={stats.negCount} color={C.red}    sub={`${(stats.negCount/stats.total*100).toFixed(0)}%`}/>
            </div>
          </div>
          <div>
            <div style={{fontFamily:'Syne,sans-serif',fontWeight:700,fontSize:13,color:C.gold,marginBottom:8}}>RISK SIGNALS</div>
            <RiskBar label="AVG URGENCY SCORE"   value={stats.avgUrgency} color={C.orange}/>
            <RiskBar label="AVG CHURN RISK"      value={stats.avgChurn}   color={C.red}/>
            <div style={{fontSize:11,color:C.t2,marginTop:6}}>{stats.highRisk} documents flagged as high-risk (≥ 6/10).</div>
          </div>
        </div>

        {stats.voice.pains.length>0&&(
          <>
            <div style={{fontFamily:'Syne,sans-serif',fontWeight:800,fontSize:14,color:C.red,marginBottom:12}}>VOICE OF CUSTOMER</div>
            <div style={{display:'grid',gridTemplateColumns:'1fr 1fr 1fr',gap:14,marginBottom:20}}>
              {[{title:'TOP PAIN POINTS',items:stats.voice.pains.slice(0,5),color:C.red},{title:'TOP REQUESTS',items:stats.voice.reqs.slice(0,5),color:C.cyan},{title:'TOP PRAISE',items:stats.voice.praises.slice(0,5),color:C.green}].map(({title,items,color})=>(
                <div key={title} style={{background:C.raised,borderRadius:4,padding:12,border:`1px solid ${color}22`}}>
                  <div style={{fontSize:9,color,fontFamily:'JetBrains Mono,monospace',fontWeight:700,marginBottom:8}}>{title}</div>
                  {items.length===0?<div style={{fontSize:10,color:C.t4}}>None detected</div>:items.map((p,i)=><div key={i} style={{fontSize:10,color:C.t2,padding:'3px 0',borderBottom:`1px solid ${C.b0}`}}>{i+1}. {p.text} <span style={{color,fontWeight:700}}>×{p.count}</span></div>)}
                </div>
              ))}
            </div>
          </>
        )}

        <div style={{fontFamily:'Syne,sans-serif',fontWeight:800,fontSize:14,color:C.cyan,marginBottom:12}}>TOP TERMS</div>
        <div style={{display:'grid',gridTemplateColumns:'repeat(5,1fr)',gap:8,marginBottom:20}}>
          {stats.topWords.slice(0,10).map((w,i)=><MB key={w.word} label={`#${i+1}`} value={w.word} color={PALETTE[i%PALETTE.length]} sub={`${w.count} times`}/>)}
        </div>

        {notes.length>0&&(
          <>
            <div style={{fontFamily:'Syne,sans-serif',fontWeight:800,fontSize:14,color:C.gold,marginBottom:12}}>RESEARCH NOTES</div>
            {notes.map(note=>(
              <div key={note.id} style={{background:C.raised,border:`1px solid ${C.gold}33`,borderLeft:`3px solid ${C.gold}`,borderRadius:3,padding:'10px 13px',marginBottom:8,display:'flex',justifyContent:'space-between',alignItems:'flex-start'}}>
                <div>
                  <span style={{fontSize:8,color:C.gold,fontFamily:'JetBrains Mono,monospace',fontWeight:700,marginRight:8}}>[{note.tag.toUpperCase()}]</span>
                  <span style={{fontSize:12,color:C.t1}}>{note.text}</span>
                </div>
                <button onClick={()=>setNotes(p=>p.filter(n=>n.id!==note.id))} style={{background:'none',border:'none',color:C.red,cursor:'pointer',fontSize:16,flexShrink:0}}>×</button>
              </div>
            ))}
          </>
        )}
      </div>

      <Card>
        <Head label="ADD RESEARCH NOTE" accent={C.gold}/>
        <Body>
          <div style={{display:'flex',gap:6,marginBottom:8,flexWrap:'wrap'}}>
            {['finding','insight','hypothesis','warning','todo'].map(t=>(
              <button key={t} onClick={()=>setNoteTag(t)} style={{padding:'4px 10px',border:`1px solid ${noteTag===t?C.gold:C.b1}`,background:noteTag===t?C.gold+'18':'transparent',color:noteTag===t?C.gold:C.t3,fontFamily:'JetBrains Mono,monospace',fontSize:8,fontWeight:700,borderRadius:2,cursor:'pointer'}}>{t.toUpperCase()}</button>
            ))}
          </div>
          <div style={{display:'flex',gap:8}}>
            <input value={noteText} onChange={e=>setNoteText(e.target.value)} onKeyDown={e=>e.key==='Enter'&&noteText.trim()&&(setNotes(p=>[...p,{id:Date.now(),text:noteText.trim(),tag:noteTag,ts:new Date().toISOString()}]),setNoteText(''))} placeholder="Your insight, finding, or hypothesis…" style={{flex:1,background:C.deep,border:`1px solid ${C.b2}`,color:C.t1,padding:'8px 12px',fontSize:11,fontFamily:'inherit',borderRadius:3,outline:'none'}}/>
            <Btn onClick={()=>{if(noteText.trim()){setNotes(p=>[...p,{id:Date.now(),text:noteText.trim(),tag:noteTag,ts:new Date().toISOString()}]);setNoteText('');}}} color={C.gold}>ADD</Btn>
          </div>
        </Body>
      </Card>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// ROOT — NLPDeepLab receives subTab from the parent hub, NO internal nav
// ─────────────────────────────────────────────────────────────────────────────
export default function NLPDeepLab({ ds, subTab='deep_setup', experiment, onUpdate, dashboardCards }) {
  const [config,setConfig]=useState(()=>experiment?.deepNlpConfig||{
    primaryCol:'', groupCol:'', dateCol:'', sampleSize:1000, useStopwords:true,
  });

  const onConfigChange=useCallback(patch=>{
    setConfig(prev=>{ const next={...prev,...patch}; onUpdate?.({deepNlpConfig:next}); return next; });
  },[onUpdate]);

  const texts=useMemo(()=>{
    if(!ds||!config.primaryCol)return[];
    return ds.rows.slice(0,config.sampleSize).map(r=>String(r[config.primaryCol]||'').trim()).filter(t=>t.length>0);
  },[ds,config.primaryCol,config.sampleSize]);

  const rows=useMemo(()=>ds?ds.rows.slice(0,config.sampleSize):[],[ds,config.sampleSize]);

  if(!ds) return(
    <div style={{padding:60,textAlign:'center',color:C.t4,fontFamily:'JetBrains Mono,monospace',fontSize:11}}>
      <div style={{fontSize:32,marginBottom:12,opacity:0.3}}>🔬</div>
      <div>Load a dataset first — use the DATA SOURCE tab.</div>
    </div>
  );

  const shared={texts,rows,col:config.primaryCol,config,ds,experiment,dashboardCards};

  return(
    <>
      {subTab==='deep_setup'    &&<DeepSetupTab  ds={ds} config={config} onConfigChange={onConfigChange}/>}
      {subTab==='deep_emotion'  &&<EmotionTab    {...shared}/>}
      {subTab==='deep_intent'   &&<IntentTab     {...shared}/>}
      {subTab==='deep_clusters' &&<ClustersTab   {...shared}/>}
      {subTab==='deep_trends'   &&<TrendsTab     {...shared}/>}
      {subTab==='deep_signals'  &&<SignalsTab     {...shared}/>}
      {subTab==='deep_voice'    &&<VoiceTab      {...shared}/>}
      {subTab==='deep_compare'  &&<CompareTab    {...shared}/>}
      {subTab==='deep_annotate' &&<AnnotateTab   {...shared}/>}
      {subTab==='report'        &&<DeepReportTab {...shared}/>}
    </>
  );
}
